import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc, query, where, onSnapshot, serverTimestamp } from 'firebase/firestore';

const generateId = () => Math.random().toString(36).substr(2, 9);
const generateInviteCode = () => { const c = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; let code = 'TPC-'; for (let i = 0; i < 6; i++) code += c.charAt(Math.floor(Math.random() * c.length)); return code; };

// Image compression helper for luggage tags (with fallback to original)
const compressImage = (file, maxWidth = 800, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const originalBase64 = e.target.result;

      // Try to compress, but fallback to original if it fails
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        } catch (err) {
          console.warn('[Compress] Canvas failed, using original:', err);
          resolve(originalBase64);
        }
      };
      img.onerror = () => {
        console.warn('[Compress] Image load failed, using original base64');
        resolve(originalBase64); // Fallback to original instead of rejecting
      };
      img.src = originalBase64;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

// Print/PDF Helper Function
const generatePrintableItinerary = (trip, hotels, myDetails = null, userName = null) => {
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const formatShortDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const getTonightHotel = (date) => hotels?.find(h => h.startDate <= date && h.endDate > date);

  // Sort activities: Morning dest (1) → Lunch (2) → Afternoon dest (3) → Dinner (4) → Evening dest (5)
  const sortActivities = (day) => {
    const getOrder = (item) => {
      if (item.itemType === 'destination') {
        const t = item.timeOfDay?.[0];
        if (t === 'Morning') return 1 + (item.displayOrder || 1) * 0.001;
        if (t === 'Afternoon') return 3 + (item.displayOrder || 1) * 0.001;
        if (t === 'Evening') return 5 + (item.displayOrder || 1) * 0.001;
        return 3;
      }
      if (item.itemType === 'dining') {
        if (item.mealType === 'Lunch') return 2;
        if (item.mealType === 'Dinner') return 4;
        return 3;
      }
      return 3;
    };
    const activities = [
      ...(day.destinations?.map(d => ({ ...d, itemType: 'destination' })) || []),
      ...(day.dining?.map(d => ({ ...d, itemType: 'dining' })) || [])
    ];
    return activities.sort((a, b) => getOrder(a) - getOrder(b));
  };

  let html = `<!DOCTYPE html><html><head><title>${trip?.name || 'Trip'} Itinerary</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Georgia, serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #1F2937; line-height: 1.5; }
  h1 { font-size: 28px; color: #3B82F6; margin-bottom: 4px; }
  h2 { font-size: 20px; color: #1E40AF; margin: 24px 0 12px; padding-bottom: 8px; border-bottom: 2px solid #3B82F6; }
  h3 { font-size: 16px; color: #374151; margin: 16px 0 8px; }
  .header { text-align: center; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 3px solid #3B82F6; }
  .subtitle { color: #6B7280; font-size: 16px; margin-top: 4px; }
  .dates { color: #3B82F6; font-size: 14px; margin-top: 8px; font-weight: 600; }
  .day { margin-bottom: 32px; page-break-inside: avoid; }
  .day-header { background: #EFF6FF; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; }
  .day-num { font-weight: 700; color: #3B82F6; }
  .day-date { color: #6B7280; font-size: 14px; }
  .hotel-box { background: #DBEAFE; padding: 16px; border-radius: 8px; margin-bottom: 16px; border-left: 4px solid #3B82F6; }
  .hotel-box h4 { color: #1E40AF; margin-bottom: 8px; }
  .activity { padding: 12px 16px; margin-bottom: 12px; border-radius: 8px; border-left: 4px solid; }
  .destination { background: #ECFDF5; border-color: #047857; }
  .dining { background: #FDF2F8; border-color: #DB2777; }
  .activity-name { font-weight: 600; margin-bottom: 4px; }
  .destination .activity-name { color: #047857; }
  .dining .activity-name { color: #DB2777; }
  .activity-detail { font-size: 13px; color: #4B5563; margin: 2px 0; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; margin-right: 8px; }
  .badge-time { background: #D1FAE5; color: #065F46; }
  .badge-meal { background: #DB2777; color: white; }
  .personal-section { margin-top: 40px; padding-top: 24px; border-top: 3px solid #3B82F6; }
  .flight-box { background: #F0F9FF; padding: 12px 16px; border-radius: 8px; margin-bottom: 8px; border-left: 4px solid #0369A1; }
  .room-box { background: #EFF6FF; padding: 12px 16px; border-radius: 8px; margin-bottom: 8px; border-left: 4px solid #3B82F6; }
  .section-label { font-size: 12px; color: #6B7280; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
  @media print { body { padding: 20px; } .day { page-break-inside: avoid; } }
</style></head><body>`;

  // Header
  html += `<div class="header">
    <h1>🌴 ${trip?.name || 'Trip Itinerary'}</h1>
    <div class="subtitle">${trip?.destination || ''}</div>
    ${trip?.startDate && trip?.endDate ? `<div class="dates">${formatShortDate(trip.startDate)} — ${formatShortDate(trip.endDate)}</div>` : ''}
    ${userName ? `<div class="subtitle" style="margin-top:12px;">Prepared for: ${userName}</div>` : ''}
  </div>`;

  // Days
  trip?.days?.forEach(day => {
    const hotel = getTonightHotel(day.date);
    const activities = sortActivities(day);

    html += `<div class="day">
      <div class="day-header">
        <span class="day-num">Day ${day.dayNumber}${day.city ? ` — ${day.city}` : ''}</span>
        <div class="day-date">${formatDate(day.date)}</div>
      </div>`;

    // Tonight's Hotel
    if (hotel) {
      html += `<div class="hotel-box">
        <div class="section-label">🏨 Tonight's Stay</div>
        <h4>${hotel.name}</h4>
        ${hotel.address ? `<div class="activity-detail">📍 ${hotel.address}</div>` : ''}
        <div class="activity-detail">
          ${hotel.checkIn ? `Check-in: ${hotel.checkIn}` : ''}
          ${hotel.checkIn && hotel.checkOut ? ' | ' : ''}
          ${hotel.checkOut ? `Check-out: ${hotel.checkOut}` : ''}
        </div>
        ${hotel.breakfastHours ? `<div class="activity-detail">🍳 Breakfast: ${hotel.breakfastHours}</div>` : ''}
      </div>`;
    }

    // Activities
    activities.forEach(item => {
      if (item.itemType === 'destination') {
        html += `<div class="activity destination">
          <div class="activity-name">📍 ${item.name}</div>
          ${item.timeOfDay?.length ? `<span class="badge badge-time">${item.timeOfDay.join(', ')}</span>` : ''}
          ${item.venueType ? `<span class="badge badge-time">${item.venueType}</span>` : ''}
          ${item.address ? `<div class="activity-detail">📍 ${item.address}</div>` : ''}
          ${item.hours ? `<div class="activity-detail">🕐 Hours: ${item.hours}</div>` : ''}
          ${item.remarks ? `<div class="activity-detail">📝 ${item.remarks}</div>` : ''}
        </div>`;
      } else {
        html += `<div class="activity dining">
          <div class="activity-name">🍽️ ${item.name}</div>
          ${item.mealType ? `<span class="badge badge-meal">${item.mealType}</span>` : ''}
          ${item.cuisine ? `<span class="badge badge-time">${item.cuisine}</span>` : ''}
          ${item.address ? `<div class="activity-detail">📍 ${item.address}</div>` : ''}
          ${item.mealTime ? `<div class="activity-detail">🕐 Reservation: ${item.mealTime}</div>` : ''}
          ${item.hours ? `<div class="activity-detail">Hours: ${item.hours}</div>` : ''}
          ${(item.adults || item.students || item.infants) ? `<div class="activity-detail">👥 Party: ${[item.adults && item.adults + ' adult(s)', item.students && item.students + ' student(s)', item.infants && item.infants + ' infant(s)'].filter(Boolean).join(', ')}</div>` : ''}
          ${item.seating ? `<div class="activity-detail">Seating: ${item.seating}</div>` : ''}
          ${item.menu ? `<div class="activity-detail">Menu: ${item.menu}</div>` : ''}
          ${item.remarks ? `<div class="activity-detail">📝 ${item.remarks}</div>` : ''}
        </div>`;
      }
    });

    if (!hotel && activities.length === 0) {
      html += `<div class="activity-detail" style="color:#9CA3AF;font-style:italic;padding:16px;">No activities planned for this day.</div>`;
    }

    html += `</div>`;
  });

  // Personal Travel Details (for Insiders)
  if (myDetails) {
    html += `<div class="personal-section">
      <h2>✈️ My Travel Details</h2>`;

    // Flights
    if (myDetails.flights?.length > 0) {
      html += `<h3>Flights</h3>`;
      myDetails.flights.forEach(f => {
        html += `<div class="flight-box">
          <div style="font-weight:600;">${f.airline || ''} ${f.flightNumber || ''}</div>
          <div class="activity-detail">${f.depAirport || ''} → ${f.arrAirport || ''}</div>
          ${f.flightDate ? `<div class="activity-detail">📅 ${f.flightDate}${f.flightDepTime ? ` | Depart: ${f.flightDepTime}` : ''}${f.flightArrTime ? ` | Arrive: ${f.flightArrTime}` : ''}</div>` : ''}
        </div>`;
      });
    }

    // Room Assignments
    if (myDetails.hotels?.length > 0) {
      html += `<h3>Room Assignments</h3>`;
      myDetails.hotels.forEach(h => {
        html += `<div class="room-box">
          <div style="font-weight:600;">🏨 ${h.hotelName || ''}</div>
          ${h.roomType ? `<div class="activity-detail">Room: ${h.roomType}</div>` : ''}
          ${h.hotelConfirm ? `<div class="activity-detail">Confirmation #: ${h.hotelConfirm}</div>` : ''}
          ${h.sharingWith ? `<div class="activity-detail">Sharing with: ${h.sharingWith}</div>` : ''}
          ${h.hotelGuests ? `<div class="activity-detail">Guests: ${h.hotelGuests}</div>` : ''}
          <div class="activity-detail">Breakfast: ${h.breakfastIncluded ? 'Included' : 'Not included'}</div>
          ${h.hotelRemarks ? `<div class="activity-detail">📝 ${h.hotelRemarks}</div>` : ''}
        </div>`;
      });
    }

    html += `</div>`;
  }

  html += `<div style="text-align:center;margin-top:40px;padding-top:20px;border-top:1px solid #E5E7EB;color:#9CA3AF;font-size:12px;">
    Generated by Trip Concierge • ${new Date().toLocaleDateString()}
  </div></body></html>`;

  // Open print window
  const printWindow = window.open('', '_blank');
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 250);
};

export default function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState([]);
  const [families, setFamilies] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [tripHotels, setTripHotels] = useState([]);
  const [memberDetails, setMemberDetails] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [view, setView] = useState('trips');
  const [notif, setNotif] = useState(null);
  const [showConverter, setShowConverter] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // localStorage helpers
  const saveToLocalStorage = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.warn('localStorage save failed:', e);
    }
  };

  const loadFromLocalStorage = (key) => {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.warn('localStorage load failed:', e);
      return null;
    }
  };

  // Check if offline and show error - returns true if offline
  const offlineCheck = () => {
    if (!navigator.onLine) {
      notify('This action requires an internet connection', 'error');
      return true;
    }
    return false;
  };

  const notify = (msg, type = 'success') => { setNotif({ msg, type }); setTimeout(() => setNotif(null), 3000); };
  const inp = { width: '100%', padding: 12, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' };
  const btn = { padding: '12px 24px', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' };
  const btn2 = { padding: '12px 24px', backgroundColor: 'transparent', color: '#6B7280', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 15, cursor: 'pointer' };

  // Auth listener - with localStorage backup for offline access
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        if (navigator.onLine) {
          const userDoc = await getDoc(doc(db, 'users', u.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            saveToLocalStorage('tc_userData', data);
          }
        } else {
          const cachedUserData = loadFromLocalStorage('tc_userData');
          if (cachedUserData) setUserData(cachedUserData);
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Data listeners - with localStorage backup for offline access
  useEffect(() => {
    if (!user) return;

    // If offline, load from localStorage
    if (!navigator.onLine) {
      setTrips(loadFromLocalStorage('tc_trips') || []);
      setFamilies(loadFromLocalStorage('tc_families') || []);
      setInvitations(loadFromLocalStorage('tc_invitations') || []);
      setTripHotels(loadFromLocalStorage('tc_tripHotels') || []);
      setMemberDetails(loadFromLocalStorage('tc_memberDetails') || []);
      setAnnouncements(loadFromLocalStorage('tc_announcements') || []);
      setMessages(loadFromLocalStorage('tc_messages') || []);
      return;
    }

    // Online: use Firebase listeners and save to localStorage
    const unsubs = [
      onSnapshot(collection(db, 'trips'), s => {
        const data = s.docs.map(d => ({ id: d.id, ...d.data() }));
        setTrips(data);
        saveToLocalStorage('tc_trips', data);
      }),
      onSnapshot(collection(db, 'families'), s => {
        const data = s.docs.map(d => ({ id: d.id, ...d.data() }));
        setFamilies(data);
        saveToLocalStorage('tc_families', data);
      }),
      onSnapshot(collection(db, 'invitations'), s => {
        const data = s.docs.map(d => ({ id: d.id, ...d.data() }));
        setInvitations(data);
        saveToLocalStorage('tc_invitations', data);
      }),
      onSnapshot(collection(db, 'tripHotels'), s => {
        const data = s.docs.map(d => ({ id: d.id, ...d.data() }));
        setTripHotels(data);
        saveToLocalStorage('tc_tripHotels', data);
      }),
      onSnapshot(collection(db, 'memberDetails'), s => {
        const data = s.docs.map(d => ({ id: d.id, ...d.data() }));
        setMemberDetails(data);
        saveToLocalStorage('tc_memberDetails', data);
      }),
      onSnapshot(collection(db, 'announcements'), s => {
        const data = s.docs.map(d => ({ id: d.id, ...d.data() }));
        setAnnouncements(data);
        saveToLocalStorage('tc_announcements', data);
      }),
      onSnapshot(collection(db, 'messages'), s => {
        const data = s.docs.map(d => ({ id: d.id, ...d.data() }));
        setMessages(data);
        saveToLocalStorage('tc_messages', data);
      })
    ];
    saveToLocalStorage('tc_lastSync', new Date().toISOString());
    return () => unsubs.forEach(u => u());
  }, [user]);

  // Auth functions
  const register = async (email, password, name) => {
    try {
      const admins = await getDocs(query(collection(db, 'users'), where('role', '==', 'admin')));
      if (!admins.empty) { notify('Admin already exists. Use invitation code.', 'error'); return; }
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', cred.user.uid), { email, name, role: 'admin', createdAt: serverTimestamp() });
      notify('Admin account created!');
    } catch (e) { notify(e.message, 'error'); }
  };

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      notify('Welcome back!');
    } catch (e) { notify('Invalid credentials', 'error'); }
  };

  const joinWithCode = async (code, email, password) => {
    try {
      const invSnap = await getDocs(query(collection(db, 'invitations'), where('code', '==', code.toUpperCase())));
      if (invSnap.empty) { notify('Invalid code', 'error'); return; }
      const inv = { id: invSnap.docs[0].id, ...invSnap.docs[0].data() };
      if (inv.email.toLowerCase() !== email.toLowerCase()) { notify('Email mismatch', 'error'); return; }
      if (inv.used) { notify('Code already used', 'error'); return; }
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', cred.user.uid), { email, name: inv.travelerName, role: 'traveler', tripId: inv.tripId, familyId: inv.familyId, invitationId: inv.id, createdAt: serverTimestamp() });
      await updateDoc(doc(db, 'invitations', inv.id), { used: true, usedBy: cred.user.uid });
      notify(`Welcome, ${inv.travelerName}!`);
    } catch (e) { notify(e.message, 'error'); }
  };

  const logout = async () => { await signOut(auth); setSelectedTrip(null); setView('trips'); notify('Signed out', 'info'); };

  // Trip functions
  const createTrip = async (data) => {
    if (offlineCheck()) return;
    const id = generateId();
    const days = [];
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    for (let d = new Date(start), n = 1; d <= end; d.setDate(d.getDate() + 1), n++) {
      days.push({ id: generateId(), dayNumber: n, date: d.toISOString().split('T')[0], destinations: [], dining: [] });
    }
    await setDoc(doc(db, 'trips', id), { ...data, days, createdAt: serverTimestamp() });
    setSelectedTrip(id);
    notify('Trip created!');
  };

  const deleteTrip = async (id) => {
    if (offlineCheck()) return;
    if (!window.confirm('Delete trip?')) return;
    await deleteDoc(doc(db, 'trips', id));
    setSelectedTrip(null);
    notify('Deleted', 'info');
  };

  const updateTrip = async (id, data) => {
    if (offlineCheck()) return;
    const trip = trips.find(t => t.id === id);
    if (!trip) return;

    // If dates changed, regenerate days array
    let days = trip.days;
    if (data.startDate !== trip.startDate || data.endDate !== trip.endDate) {
      days = [];
      const start = new Date(data.startDate);
      const end = new Date(data.endDate);
      for (let d = new Date(start), n = 1; d <= end; d.setDate(d.getDate() + 1), n++) {
        // Try to preserve existing day data if date matches
        const existingDay = trip.days?.find(day => day.date === d.toISOString().split('T')[0]);
        if (existingDay) {
          days.push({ ...existingDay, dayNumber: n });
        } else {
          days.push({ id: generateId(), dayNumber: n, date: d.toISOString().split('T')[0], destinations: [], dining: [] });
        }
      }
    }

    await updateDoc(doc(db, 'trips', id), { ...data, days });
    notify('Trip updated!');
  };

  // Family functions
  const createFamily = async (tripId, name) => {
    if (offlineCheck()) return;
    await setDoc(doc(db, 'families', generateId()), { tripId, name, createdAt: serverTimestamp() });
    notify('Family created!');
  };

  const deleteFamily = async (id) => {
    if (offlineCheck()) return;
    await deleteDoc(doc(db, 'families', id));
    notify('Deleted', 'info');
  };

  // Invitation functions
  const createInvitation = async (tripId, familyId, name, email, customCode, age, dietaryPreferences, memberType) => {
    if (offlineCheck()) return;
    const code = customCode?.toUpperCase() || generateInviteCode();
    await setDoc(doc(db, 'invitations', generateId()), { tripId, familyId, travelerName: name, email, code, used: false, age: age || 'Adult', dietaryPreferences: dietaryPreferences || '', memberType: memberType || 'insider', createdAt: serverTimestamp() });
    notify('Member added!');
  };

  const deleteInvitation = async (id) => {
    if (offlineCheck()) return;
    await deleteDoc(doc(db, 'invitations', id));
    notify('Deleted', 'info');
  };

  // Hotel functions
  const createHotel = async (tripId, data) => {
    if (offlineCheck()) return;
    await setDoc(doc(db, 'tripHotels', generateId()), { tripId, ...data, createdAt: serverTimestamp() });
    notify('Hotel added!');
  };

  const deleteHotel = async (id) => {
    if (offlineCheck()) return;
    await deleteDoc(doc(db, 'tripHotels', id));
    notify('Deleted', 'info');
  };

  const updateHotel = async (id, data) => {
    if (offlineCheck()) return;
    await updateDoc(doc(db, 'tripHotels', id), data);
    notify('Hotel updated!');
  };

  // Day update function
  const updateDay = async (tripId, dayId, type, action, data, itemId) => {
    if (offlineCheck()) return;
    const trip = trips.find(t => t.id === tripId);
    if (!trip) return;
    const days = trip.days.map(d => {
      if (d.id !== dayId) return d;
      // Handle city field update
      if (type === 'city') {
        return { ...d, city: data.city };
      }
      let items = [...(d[type] || [])];
      if (action === 'add') items.push({ id: generateId(), ...data });
      else if (action === 'update') items = items.map(i => i.id === itemId ? { ...i, ...data } : i);
      else if (action === 'delete') items = items.filter(i => i.id !== itemId);
      return { ...d, [type]: items };
    });
    await updateDoc(doc(db, 'trips', tripId), { days });
    notify(action === 'delete' ? 'Removed' : 'Saved!', action === 'delete' ? 'info' : 'success');
  };

  // Member details functions
  const addMemberFlight = async (invId, familyId, data) => {
    if (offlineCheck()) return;
    const existing = memberDetails.find(m => m.invitationId === invId);
    if (existing) {
      await updateDoc(doc(db, 'memberDetails', existing.id), { flights: [...(existing.flights || []), { id: generateId(), ...data }] });
    } else {
      await setDoc(doc(db, 'memberDetails', generateId()), { invitationId: invId, familyId, flights: [{ id: generateId(), ...data }], hotels: [] });
    }
    notify('Flight added!');
  };

  const deleteMemberFlight = async (invId, flightId) => {
    if (offlineCheck()) return;
    const existing = memberDetails.find(m => m.invitationId === invId);
    if (existing) {
      await updateDoc(doc(db, 'memberDetails', existing.id), { flights: (existing.flights || []).filter(f => f.id !== flightId) });
      notify('Removed', 'info');
    }
  };

  const addMemberHotel = async (invId, familyId, data) => {
    if (offlineCheck()) return;
    const existing = memberDetails.find(m => m.invitationId === invId);
    if (existing) {
      await updateDoc(doc(db, 'memberDetails', existing.id), { hotels: [...(existing.hotels || []), { id: generateId(), ...data }] });
    } else {
      await setDoc(doc(db, 'memberDetails', generateId()), { invitationId: invId, familyId, flights: [], hotels: [{ id: generateId(), ...data }] });
    }
    notify('Hotel added!');
  };

  const deleteMemberHotel = async (invId, hotelId) => {
    if (offlineCheck()) return;
    const existing = memberDetails.find(m => m.invitationId === invId);
    if (existing) {
      await updateDoc(doc(db, 'memberDetails', existing.id), { hotels: (existing.hotels || []).filter(h => h.id !== hotelId) });
      notify('Removed', 'info');
    }
  };

  const updateMemberFlight = async (invId, flightId, data) => {
    if (offlineCheck()) return;
    const existing = memberDetails.find(m => m.invitationId === invId);
    if (existing) {
      await updateDoc(doc(db, 'memberDetails', existing.id), {
        flights: (existing.flights || []).map(f => f.id === flightId ? { ...f, ...data } : f)
      });
      notify('Flight updated!');
    }
  };

  const updateMemberHotel = async (invId, hotelId, data) => {
    if (offlineCheck()) return;
    const existing = memberDetails.find(m => m.invitationId === invId);
    if (existing) {
      await updateDoc(doc(db, 'memberDetails', existing.id), {
        hotels: (existing.hotels || []).map(h => h.id === hotelId ? { ...h, ...data } : h)
      });
      notify('Room updated!');
    }
  };

  const updateFamily = async (id, name) => {
    if (offlineCheck()) return;
    await updateDoc(doc(db, 'families', id), { name });
    notify('Family updated!');
  };

  const updateInvitation = async (id, data) => {
    if (offlineCheck()) return;
    await updateDoc(doc(db, 'invitations', id), data);
    notify('Member updated!');
  };

  const updateLuggageTag = async (invId, familyId, imageData) => {
    if (offlineCheck()) return;
    const existing = memberDetails.find(m => m.invitationId === invId);
    if (existing) {
      await updateDoc(doc(db, 'memberDetails', existing.id), { luggageTag: imageData });
    } else {
      const newId = generateId();
      await setDoc(doc(db, 'memberDetails', newId), { invitationId: invId, familyId, flights: [], hotels: [], luggageTag: imageData });
    }
    notify(imageData ? 'Luggage tag uploaded!' : 'Luggage tag removed', imageData ? 'success' : 'info');
  };

  // Announcements
  const createAnnouncement = async (tripId, data) => {
    if (offlineCheck()) return;
    await setDoc(doc(db, 'announcements', generateId()), { tripId, ...data, createdAt: new Date().toISOString() });
    notify('Posted!');
  };

  const deleteAnnouncement = async (id) => {
    if (offlineCheck()) return;
    await deleteDoc(doc(db, 'announcements', id));
    notify('Deleted', 'info');
  };

  // Messages (Thread-based communication)
  const createThread = async (tripId, senderName, senderEmail, messageText, senderRole = 'traveler') => {
    if (offlineCheck()) return;
    await setDoc(doc(db, 'messages', generateId()), {
      tripId,
      travelerName: senderName,
      travelerEmail: senderEmail,
      messages: [{
        id: generateId(),
        sender: senderName,
        senderRole,
        text: messageText,
        timestamp: new Date().toISOString()
      }],
      read: senderRole === 'admin',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    notify('Message sent!');
  };

  const addToThread = async (threadId, senderName, messageText, senderRole) => {
    if (offlineCheck()) return;
    const thread = messages.find(m => m.id === threadId);
    if (!thread) return;
    if (thread.messages?.length >= 10) { notify('Thread limit reached', 'error'); return; }
    // Handle old message format migration
    const existingMessages = thread.messages || (thread.message ? [{
      id: generateId(),
      sender: thread.senderName,
      senderRole: 'traveler',
      text: thread.message,
      timestamp: thread.createdAt
    }, ...(thread.reply ? [{
      id: generateId(),
      sender: 'Organizer',
      senderRole: 'admin',
      text: thread.reply,
      timestamp: thread.repliedAt
    }] : [])] : []);

    await updateDoc(doc(db, 'messages', threadId), {
      messages: [...existingMessages, {
        id: generateId(),
        sender: senderName,
        senderRole,
        text: messageText,
        timestamp: new Date().toISOString()
      }],
      read: senderRole === 'admin',
      updatedAt: new Date().toISOString()
    });
    notify('Message sent!');
  };

  const markThreadRead = async (threadId) => {
    if (!navigator.onLine) return; // Silent fail for marking read when offline
    await updateDoc(doc(db, 'messages', threadId), { read: true });
  };

  const deleteThread = async (id) => {
    if (offlineCheck()) return;
    await deleteDoc(doc(db, 'messages', id));
    notify('Deleted', 'info');
  };

  // Notification
  const Notif = () => notif && (
    <div style={{ position: 'fixed', top: 20, right: 20, padding: '16px 24px', backgroundColor: notif.type === 'error' ? '#FEE2E2' : notif.type === 'info' ? '#E0F2FE' : '#ECFDF5', color: notif.type === 'error' ? '#991B1B' : notif.type === 'info' ? '#0369A1' : '#166534', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, fontWeight: 500 }}>{notif.msg}</div>
  );

  // Offline Banner
  const OfflineBanner = () => isOffline && (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, padding: '8px 16px', backgroundColor: '#F3F4F6', color: '#6B7280', textAlign: 'center', fontSize: 13, zIndex: 999, borderBottom: '1px solid #E5E7EB' }}>
      📴 Offline Mode - Viewing cached data
    </div>
  );

  // Loading
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAF8F5', fontFamily: 'Georgia, serif' }}>
      <div style={{ textAlign: 'center' }}><div style={{ fontSize: 48, color: '#3B82F6' }}>✦</div><p style={{ color: '#6B7280' }}>Loading...</p></div>
    </div>
  );

  // Login screen
  if (!user) return <LoginScreen onLogin={login} onRegister={register} onJoin={joinWithCode} notify={notify} inp={inp} btn={btn} btn2={btn2} Notif={Notif} />;

  // Main app
  const trip = trips.find(t => t.id === selectedTrip);
  const isAdmin = userData?.role === 'admin';
  const tripFamilies = families.filter(f => f.tripId === selectedTrip);
  const tripInvs = invitations.filter(i => i.tripId === selectedTrip);
  const tripHotelsList = tripHotels.filter(h => h.tripId === selectedTrip);
  const tripAnns = announcements.filter(a => a.tripId === selectedTrip);
  const tripMsgs = messages.filter(m => m.tripId === selectedTrip);

  // Traveler view
  if (!isAdmin) {
    const myTrip = trips.find(t => t.id === userData?.tripId);
    const myDetails = memberDetails.find(m => m.invitationId === userData?.invitationId) || { flights: [], hotels: [], luggageTag: null };
    const myHotels = tripHotels.filter(h => h.tripId === userData?.tripId);
    const myAnns = announcements.filter(a => a.tripId === userData?.tripId);
    const myMsgs = messages.filter(m => m.tripId === userData?.tripId && m.travelerEmail === userData?.email);
    const myFamily = families.find(f => f.id === userData?.familyId);
    const familyMembers = invitations.filter(i => i.familyId === userData?.familyId);
    const familyMemberDetails = memberDetails.filter(m => familyMembers.some(fm => fm.id === m.invitationId));

    return <TravelerDashboard user={userData} myTrip={myTrip} myDetails={myDetails} myHotels={myHotels} myAnns={myAnns} myMsgs={myMsgs} myFamily={myFamily} familyMembers={familyMembers} familyMemberDetails={familyMemberDetails} onCreateThread={(msg) => createThread(userData?.tripId, userData?.name, userData?.email, msg, 'traveler')} onAddToThread={(threadId, msg) => addToThread(threadId, userData?.name, msg, 'traveler')} logout={logout} btn={btn} inp={inp} Notif={Notif} notify={notify} isOffline={isOffline} OfflineBanner={OfflineBanner} />;
  }

  // Admin view
  return (
    <div style={{ minHeight: '100vh', background: '#FAF8F5', fontFamily: 'Georgia, serif', paddingTop: isOffline ? 36 : 0 }}>
      <Notif />
      <OfflineBanner />
      {showConverter && <CurrencyConverter onClose={() => setShowConverter(false)} />}
      <header style={{ backgroundColor: 'white', borderBottom: '1px solid #E5E7EB', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 28, color: '#3B82F6' }}>✦</span>
          <div><h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Trip Concierge</h1><p style={{ fontSize: 12, color: '#3B82F6', margin: 0, fontStyle: 'italic' }}>Organizer</p></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setShowConverter(true)} style={{ padding: '8px 12px', backgroundColor: '#EFF6FF', color: '#3B82F6', border: '1px solid #3B82F6', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>💱 Currency</button>
          <span>{userData?.name}</span>
          <button onClick={logout} style={{ ...btn2, padding: '8px 16px' }}>Sign Out</button>
        </div>
      </header>

      <main style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
        {view === 'trips' && !selectedTrip && <TripList trips={trips} onSelect={setSelectedTrip} onCreate={createTrip} onDelete={deleteTrip} notify={notify} inp={inp} btn={btn} btn2={btn2} />}
        
        {selectedTrip && view === 'trips' && <Dashboard trip={trip} families={tripFamilies} invitations={tripInvs} hotels={tripHotelsList} announcements={tripAnns} messages={tripMsgs} onBack={() => setSelectedTrip(null)} onDelete={() => deleteTrip(selectedTrip)} onUpdate={(data) => updateTrip(selectedTrip, data)} setView={setView} btn={btn} btn2={btn2} inp={inp} notify={notify} />}
        
        {view === 'itinerary' && <Itinerary trip={trip} hotels={tripHotelsList} onBack={() => setView('trips')} onUpdate={(dayId, type, action, data, itemId) => updateDay(selectedTrip, dayId, type, action, data, itemId)} notify={notify} inp={inp} btn={btn} btn2={btn2} />}
        
        {view === 'hotels' && <Hotels hotels={tripHotelsList} onCreate={(data) => createHotel(selectedTrip, data)} onUpdate={updateHotel} onDelete={deleteHotel} onBack={() => setView('trips')} notify={notify} inp={inp} btn={btn} btn2={btn2} tripName={trip?.name} />}
        
        {view === 'families' && <Families trip={trip} families={tripFamilies} invitations={tripInvs} memberDetails={memberDetails} tripHotels={tripHotelsList} onCreateFamily={(name) => createFamily(selectedTrip, name)} onUpdateFamily={updateFamily} onDeleteFamily={deleteFamily} onCreateInvitation={(fId, name, email, code) => createInvitation(selectedTrip, fId, name, email, code)} onUpdateInvitation={updateInvitation} onDeleteInvitation={deleteInvitation} onAddFlight={addMemberFlight} onUpdateFlight={updateMemberFlight} onDeleteFlight={deleteMemberFlight} onAddHotel={addMemberHotel} onUpdateHotel={updateMemberHotel} onDeleteHotel={deleteMemberHotel} onUpdateLuggageTag={updateLuggageTag} onBack={() => setView('trips')} notify={notify} inp={inp} btn={btn} btn2={btn2} />}
        
        {view === 'announcements' && <Announcements announcements={tripAnns} onCreate={(data) => createAnnouncement(selectedTrip, data)} onDelete={deleteAnnouncement} onBack={() => setView('trips')} notify={notify} inp={inp} btn={btn} btn2={btn2} tripName={trip?.name} />}
        
        {view === 'messages' && <Messages messages={tripMsgs} onAddToThread={(threadId, msg) => addToThread(threadId, userData?.name || 'Organizer', msg, 'admin')} onMarkRead={markThreadRead} onDelete={deleteThread} onBack={() => setView('trips')} notify={notify} inp={inp} btn={btn} btn2={btn2} tripName={trip?.name} />}
      </main>
    </div>
  );
}

// ========== LOGIN SCREEN ==========
function LoginScreen({ onLogin, onRegister, onJoin, notify, inp, btn, btn2, Notif }) {
  const [mode, setMode] = useState('login');
  const [joinPhase, setJoinPhase] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [invitationName, setInvitationName] = useState('');
  const [adminExists, setAdminExists] = useState(true);
  const [checkingCode, setCheckingCode] = useState(false);

  // Check if admin exists on mount
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const admins = await getDocs(query(collection(db, 'users'), where('role', '==', 'admin')));
        setAdminExists(!admins.empty);
      } catch (e) {
        setAdminExists(true);
      }
    };
    checkAdmin();
  }, []);

  // Validate invitation code and get name
  const validateCode = async () => {
    if (!code.trim()) { notify('Enter invitation code', 'error'); return; }
    setCheckingCode(true);
    try {
      const invSnap = await getDocs(query(collection(db, 'invitations'), where('code', '==', code.toUpperCase())));
      if (invSnap.empty) { notify('Invalid invitation code', 'error'); setCheckingCode(false); return; }
      const inv = invSnap.docs[0].data();
      if (inv.used) { notify('This code has already been used', 'error'); setCheckingCode(false); return; }
      setInvitationName(inv.travelerName);
      setJoinPhase(2);
    } catch (e) {
      notify('Error validating code', 'error');
    }
    setCheckingCode(false);
  };

  const submit = () => {
    if (mode === 'login') { if (!email || !password) { notify('Fill all fields', 'error'); return; } onLogin(email, password); }
    else if (mode === 'register') { if (!email || !password || !name) { notify('Fill all fields', 'error'); return; } if (password.length < 6) { notify('Password min 6 chars', 'error'); return; } onRegister(email, password, name); }
    else { if (!email || !password) { notify('Fill all fields', 'error'); return; } if (password.length < 6) { notify('Password min 6 chars', 'error'); return; } onJoin(code, email, password); }
  };

  const resetJoin = () => {
    setMode('login');
    setJoinPhase(1);
    setCode('');
    setInvitationName('');
    setEmail('');
    setPassword('');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'linear-gradient(135deg, #FAF8F5 0%, #F5F0E8 100%)', fontFamily: 'Georgia, serif' }}>
      <Notif />
      <div style={{ backgroundColor: 'white', borderRadius: 24, padding: 48, width: '100%', maxWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 48, color: '#3B82F6', marginBottom: 8 }}>✦</div>
          <h1 style={{ fontSize: 28, fontWeight: 600, margin: 0 }}>Trip Concierge</h1>
          <p style={{ color: '#6B7280', margin: '8px 0 0' }}>Your digital travel portfolio</p>
        </div>

        {/* Login Mode */}
        {mode === 'login' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" style={inp} type="email" />
            <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" style={inp} type="password" onKeyDown={e => e.key === 'Enter' && submit()} />
            <button onClick={submit} style={btn}>Sign In</button>
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => { setMode('join'); setJoinPhase(1); }} style={{ background: 'none', border: 'none', color: '#3B82F6', cursor: 'pointer' }}>Join with Invitation Code</button>
              {!adminExists && (
                <p style={{ color: '#9CA3AF', fontSize: 14, marginTop: 16 }}>Trip organizer? <button onClick={() => setMode('register')} style={{ background: 'none', border: 'none', color: '#3B82F6', cursor: 'pointer' }}>Create Account</button></p>
              )}
            </div>
          </div>
        )}

        {/* Register Mode */}
        {mode === 'register' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Your Name" style={inp} />
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" style={inp} type="email" />
            <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" style={inp} type="password" onKeyDown={e => e.key === 'Enter' && submit()} />
            <button onClick={submit} style={btn}>Create Admin Account</button>
            <button onClick={() => setMode('login')} style={btn2}>Back to Sign In</button>
          </div>
        )}

        {/* Join Mode - Phase 1: Enter Code */}
        {mode === 'join' && joinPhase === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="Enter Invitation Code" style={{ ...inp, fontFamily: 'monospace', textAlign: 'center', fontSize: 18, letterSpacing: 2 }} onKeyDown={e => e.key === 'Enter' && validateCode()} />
            <button onClick={validateCode} style={btn} disabled={checkingCode}>{checkingCode ? 'Checking...' : 'Next'}</button>
            <button onClick={resetJoin} style={btn2}>Back to Sign In</button>
          </div>
        )}

        {/* Join Mode - Phase 2: Create Account */}
        {mode === 'join' && joinPhase === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ textAlign: 'center', marginBottom: 8, padding: 16, backgroundColor: '#EFF6FF', borderRadius: 12 }}>
              <p style={{ margin: 0, color: '#3B82F6', fontSize: 18, fontWeight: 600 }}>Welcome, {invitationName}!</p>
              <p style={{ margin: '8px 0 0', color: '#6B7280', fontSize: 13 }}>Create your account to join the trip</p>
            </div>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" style={inp} type="email" />
            <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Create Password" style={inp} type="password" onKeyDown={e => e.key === 'Enter' && submit()} />
            <button onClick={submit} style={btn}>Join Trip</button>
            <button onClick={() => setJoinPhase(1)} style={btn2}>Back</button>
          </div>
        )}
      </div>

      {/* Footer Credit */}
      <p style={{ marginTop: 24, color: '#9CA3AF', fontSize: 13, textAlign: 'center' }}>Trip Concierge by Jeremy Shen</p>
    </div>
  );
}

// ========== TRIP LIST ==========
function TripList({ trips, onSelect, onCreate, onDelete, notify, inp, btn, btn2 }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', destination: '', startDate: '', endDate: '' });

  const handleCreate = () => {
    if (!form.name || !form.destination || !form.startDate || !form.endDate) { notify('Fill all fields', 'error'); return; }
    onCreate(form);
    setForm({ name: '', destination: '', startDate: '', endDate: '' });
    setCreating(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>Your Trips</h2>
        <button onClick={() => setCreating(true)} style={btn}>+ New Trip</button>
      </div>
      {creating && (
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Create Trip</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Trip Name" style={inp} />
            <input value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} placeholder="Destination" style={inp} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} style={inp} />
              <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} style={inp} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setCreating(false)} style={{ ...btn2, flex: 1 }}>Cancel</button>
              <button onClick={handleCreate} style={{ ...btn, flex: 1 }}>Create</button>
            </div>
          </div>
        </div>
      )}
      {trips.length === 0 && !creating && (
        <div style={{ textAlign: 'center', padding: 60, backgroundColor: 'white', borderRadius: 16 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✈️</div>
          <p style={{ color: '#6B7280' }}>No trips yet</p>
          <button onClick={() => setCreating(true)} style={{ ...btn, marginTop: 16 }}>Create Trip</button>
        </div>
      )}
      {trips.map(t => (
        <div key={t.id} onClick={() => onSelect(t.id)} style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 16, cursor: 'pointer', border: '1px solid #E5E7EB' }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{t.name}</h3>
          <p style={{ margin: '4px 0 0', color: '#3B82F6', fontStyle: 'italic' }}>{t.destination}</p>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: '#6B7280' }}>
            📅 {t.startDate && t.endDate ? `${new Date(t.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(t.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : `${t.days?.length || 0} days`}
          </p>
        </div>
      ))}
    </div>
  );
}

// ========== DASHBOARD ==========
function Dashboard({ trip, families, invitations, hotels, announcements, messages, onBack, onDelete, onUpdate, setView, btn, btn2, inp, notify }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const joined = invitations.filter(i => i.used).length;
  const pending = invitations.filter(i => !i.used).length;
  const unreadMsgs = (messages || []).filter(m => !m.read).length;

  const startEdit = () => {
    setForm({ name: trip?.name || '', destination: trip?.destination || '', startDate: trip?.startDate || '', endDate: trip?.endDate || '' });
    setEditing(true);
  };

  const saveEdit = () => {
    if (!form.name || !form.destination || !form.startDate || !form.endDate) { notify('Fill all fields', 'error'); return; }
    if (new Date(form.endDate) < new Date(form.startDate)) { notify('End date must be after start date', 'error'); return; }
    onUpdate(form);
    setEditing(false);
  };

  const formatDateRange = () => {
    if (!trip?.startDate || !trip?.endDate) return null;
    const start = new Date(trip.startDate);
    const end = new Date(trip.endDate);
    return `${start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  return (
    <div>
      <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', padding: 0, marginBottom: 8 }}>← All Trips</button>
            <h2 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>{trip?.name}</h2>
            <p style={{ color: '#3B82F6', fontStyle: 'italic', margin: '4px 0 0' }}>{trip?.destination}</p>
            {formatDateRange() && <p style={{ color: '#6B7280', margin: '8px 0 0', fontSize: 14 }}>📅 {formatDateRange()}</p>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={startEdit} style={btn2}>Edit Trip</button>
            <button onClick={onDelete} style={{ ...btn2, color: '#DC2626', borderColor: '#FCA5A5' }}>Delete</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 32 }}>
          <div><span style={{ fontSize: 28, fontWeight: 600 }}>{trip?.days?.length || 0}</span><span style={{ display: 'block', fontSize: 14, color: '#6B7280' }}>Days</span></div>
          <div><span style={{ fontSize: 28, fontWeight: 600 }}>{families.length}</span><span style={{ display: 'block', fontSize: 14, color: '#6B7280' }}>Families</span></div>
          <div><span style={{ fontSize: 28, fontWeight: 600 }}>{joined}</span><span style={{ display: 'block', fontSize: 14, color: '#6B7280' }}>Joined</span></div>
          <div><span style={{ fontSize: 28, fontWeight: 600 }}>{pending}</span><span style={{ display: 'block', fontSize: 14, color: '#6B7280' }}>Pending</span></div>
        </div>
      </div>

      {editing && (
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Edit Trip</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Trip Name" style={inp} />
            <input value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} placeholder="Destination" style={inp} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>Start Date</label>
                <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>End Date</label>
                <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} style={inp} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setEditing(false)} style={{ ...btn2, flex: 1 }}>Cancel</button>
              <button onClick={saveEdit} style={{ ...btn, flex: 1 }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Quick Actions</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
        {[{ i: '📅', l: 'Itinerary', v: 'itinerary' }, { i: '🏨', l: 'Hotels', v: 'hotels' }, { i: '👨‍👩‍👧‍👦', l: 'Families', v: 'families' }, { i: '📢', l: 'Announce', v: 'announcements' }, { i: '💬', l: 'Messages', v: 'messages', badge: unreadMsgs }].map(item => (
          <button key={item.v} onClick={() => setView(item.v)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 24, backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: 16, cursor: 'pointer', position: 'relative' }}>
            {item.badge > 0 && <span style={{ position: 'absolute', top: 8, right: 8, backgroundColor: '#DC2626', color: 'white', borderRadius: '50%', width: 20, height: 20, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.badge}</span>}
            <span style={{ fontSize: 32 }}>{item.i}</span>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{item.l}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ========== ITINERARY ==========
function Itinerary({ trip, hotels, onBack, onUpdate, notify, inp, btn, btn2 }) {
  const [selectedDay, setSelectedDay] = useState(null);
  const [adding, setAdding] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [editingCity, setEditingCity] = useState(false);
  const [cityForm, setCityForm] = useState('');

  const day = trip?.days?.find(d => d.id === selectedDay);
  const getTonightHotel = (date) => hotels?.find(h => h.startDate <= date && h.endDate > date);
  const openMaps = (q) => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`, '_blank');

  const save = () => {
    const type = editing ? form.type : adding;
    if (!form.name) { notify('Name required', 'error'); return; }
    const { type: _, ...data } = form;
    if (editing) { onUpdate(selectedDay, type, 'update', data, editing); setEditing(null); }
    else { onUpdate(selectedDay, type, 'add', data); }
    setForm({}); setAdding(null);
  };

  const startEdit = (item, type) => { setForm({ ...item, type }); setEditing(item.id); setAdding(type); };

  const startEditCity = () => {
    setCityForm(day?.city || '');
    setEditingCity(true);
  };

  const saveCity = () => {
    onUpdate(selectedDay, 'city', 'update', { city: cityForm });
    setEditingCity(false);
  };

  // Dining form with ALL fields
  const renderDiningForm = () => (
    <>
      <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Restaurant Name *" style={inp} />
      <input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address" style={inp} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>Meal Type *</label>
          <select value={form.mealType || ''} onChange={e => setForm({ ...form, mealType: e.target.value })} style={{ ...inp, width: '100%' }}>
            <option value="">Select...</option>
            <option value="Lunch">Lunch</option>
            <option value="Dinner">Dinner</option>
          </select>
        </div>
        <input value={form.cuisine || ''} onChange={e => setForm({ ...form, cuisine: e.target.value })} placeholder="Cuisine Type (e.g., Japanese)" style={inp} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>Reservation Time</label><input type="time" value={form.mealTime || ''} onChange={e => setForm({ ...form, mealTime: e.target.value })} style={inp} /></div>
        <input value={form.hours || ''} onChange={e => setForm({ ...form, hours: e.target.value })} placeholder="Operating Hours" style={inp} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <input type="number" value={form.adults || ''} onChange={e => setForm({ ...form, adults: e.target.value })} placeholder="Adults" style={inp} />
        <input type="number" value={form.students || ''} onChange={e => setForm({ ...form, students: e.target.value })} placeholder="Students" style={inp} />
        <input type="number" value={form.infants || ''} onChange={e => setForm({ ...form, infants: e.target.value })} placeholder="Infants" style={inp} />
      </div>
      <input value={form.seating || ''} onChange={e => setForm({ ...form, seating: e.target.value })} placeholder="Seating Arrangement" style={inp} />
      <textarea value={form.menu || ''} onChange={e => setForm({ ...form, menu: e.target.value })} placeholder="Menu Selections" style={{ ...inp, minHeight: 50 }} />
      <textarea value={form.remarks || ''} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="Remarks (dress code, dietary restrictions)" style={{ ...inp, minHeight: 50 }} />
    </>
  );

  // Destination form with ALL fields
  const renderDestForm = () => (
    <>
      <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Destination Name *" style={inp} />
      <input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address" style={inp} />
      <div style={{ marginBottom: 8 }}>
        <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 8 }}>Time of Day</label>
        <div style={{ display: 'flex', gap: 16 }}>
          {['Morning', 'Afternoon', 'Evening'].map(t => (
            <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={(form.timeOfDay || []).includes(t)} onChange={e => {
                const cur = form.timeOfDay || [];
                setForm({ ...form, timeOfDay: e.target.checked ? [...cur, t] : cur.filter(x => x !== t) });
              }} />
              {t}
            </label>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <input value={form.venueType || ''} onChange={e => setForm({ ...form, venueType: e.target.value })} placeholder="Venue Type" style={inp} />
        <input value={form.hours || ''} onChange={e => setForm({ ...form, hours: e.target.value })} placeholder="Operating Hours" style={inp} />
        <div>
          <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>Display Order</label>
          <input type="number" value={form.displayOrder || 1} onChange={e => setForm({ ...form, displayOrder: parseInt(e.target.value) || 1 })} min="1" style={inp} />
        </div>
      </div>
      <textarea value={form.remarks || ''} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="Remarks" style={{ ...inp, minHeight: 50 }} />
    </>
  );

  if (selectedDay && day) {
    const dt = new Date(day.date);
    const hotel = getTonightHotel(day.date);

    // Order: Morning destinations (1) → Lunch dining (2) → Afternoon destinations (3) → Dinner dining (4) → Evening destinations (5)
    const getTimeOrder = (item) => {
      const type = item.type;
      if (type === 'destinations') {
        const timeOfDay = item.timeOfDay?.[0];
        if (timeOfDay === 'Morning') return 1 + (item.displayOrder || 1) * 0.001;
        if (timeOfDay === 'Afternoon') return 3 + (item.displayOrder || 1) * 0.001;
        if (timeOfDay === 'Evening') return 5 + (item.displayOrder || 1) * 0.001;
        return 3 + (item.displayOrder || 1) * 0.001; // Default to afternoon
      }
      if (type === 'dining') {
        if (item.mealType === 'Lunch') return 2;
        if (item.mealType === 'Dinner') return 4;
        // Fallback to time-based if no mealType
        if (item.mealTime) { const [h] = item.mealTime.split(':').map(Number); return h < 15 ? 2 : 4; }
        return 3; // Default between lunch and dinner
      }
      return 3;
    };

    const allEvents = [
      ...(day.dining || []).map(d => ({ ...d, type: 'dining' })),
      ...(day.destinations || []).map(d => ({ ...d, type: 'destinations' }))
    ].sort((a, b) => getTimeOrder(a) - getTimeOrder(b));

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <button onClick={() => setSelectedDay(null)} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>← Back</button>
          <div>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#3B82F6' }}>Day {day.dayNumber}{day.city ? ` — ${day.city}` : ''}</span>
            <span style={{ fontSize: 16, fontWeight: 600, marginLeft: 8 }}>{dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>

        {/* City Editor */}
        <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: '#6B7280' }}>📍 City/Cities for this day</div>
            {!editingCity && <button onClick={startEditCity} style={{ background: 'none', border: 'none', color: '#0369A1', cursor: 'pointer', fontSize: 12 }}>Edit</button>}
          </div>
          {editingCity ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input value={cityForm} onChange={e => setCityForm(e.target.value)} placeholder="e.g., Kyoto or Kyoto → Osaka" style={{ ...inp, flex: 1 }} />
              <button onClick={() => setEditingCity(false)} style={{ ...btn2, padding: '8px 16px' }}>Cancel</button>
              <button onClick={saveCity} style={{ ...btn, padding: '8px 16px' }}>Save</button>
            </div>
          ) : (
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{day.city || <span style={{ color: '#9CA3AF', fontWeight: 400 }}>No city set</span>}</div>
          )}
        </div>

        {/* Tonight's Hotel */}
        {hotel && (
          <div style={{ backgroundColor: '#EFF6FF', borderRadius: 12, padding: 16, marginBottom: 24, borderLeft: '4px solid #3B82F6' }}>
            <div style={{ fontSize: 12, color: '#1E40AF', marginBottom: 4 }}>🏨 Tonight's Stay</div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{hotel.name}</div>
            {hotel.startDate && hotel.endDate && <div style={{ fontSize: 13, color: '#1E40AF', marginTop: 4 }}>📅 {new Date(hotel.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(hotel.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6, fontSize: 13, color: '#4B5563' }}>
              {hotel.roomsReserved && <span>🛏️ {hotel.roomsReserved} room(s)</span>}
              {hotel.breakfastHours && <span>🍳 Breakfast: {hotel.breakfastHours}</span>}
            </div>
          </div>
        )}

        {!adding && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            <button onClick={() => { setAdding('destinations'); setForm({}); }} style={{ ...btn, flex: 1, backgroundColor: '#047857' }}>+ Destination</button>
            <button onClick={() => { setAdding('dining'); setForm({}); }} style={{ ...btn, flex: 1, backgroundColor: '#DB2777' }}>+ Dining</button>
          </div>
        )}

        {adding && (
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 24, border: `2px solid ${adding === 'dining' ? '#DB2777' : '#047857'}` }}>
            <h4 style={{ margin: '0 0 16px', color: adding === 'dining' ? '#DB2777' : '#047857' }}>{editing ? 'Edit' : 'Add'} {adding === 'dining' ? '🍽️ Dining' : '📍 Destination'}</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {adding === 'dining' ? renderDiningForm() : renderDestForm()}
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button onClick={() => { setAdding(null); setEditing(null); setForm({}); }} style={{ ...btn2, flex: 1 }}>Cancel</button>
                <button onClick={save} style={{ ...btn, flex: 1, backgroundColor: adding === 'dining' ? '#DB2777' : '#047857' }}>{editing ? 'Save' : 'Add'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Events list with full details */}
        {allEvents.map(it => {
          const isDining = it.type === 'dining';
          return (
            <div key={it.id} style={{ backgroundColor: isDining ? '#FDF2F8' : '#ECFDF5', borderRadius: 12, padding: 16, marginBottom: 12, borderLeft: `4px solid ${isDining ? '#DB2777' : '#047857'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{isDining ? '🍽️' : '📍'}</span>
                  <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                    {isDining ? it.name : <span onClick={() => openMaps(it.name)} style={{ cursor: 'pointer', textDecoration: 'underline', color: '#047857' }}>{it.name} ↗</span>}
                  </h4>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => startEdit(it, it.type)} style={{ background: 'none', border: 'none', color: '#3B82F6', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                  <button onClick={() => onUpdate(selectedDay, it.type, 'delete', {}, it.id)} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 18 }}>×</button>
                </div>
              </div>
              {it.address && <p onClick={() => openMaps(it.address)} style={{ margin: '0 0 8px', fontSize: 13, color: '#3B82F6', cursor: 'pointer', textDecoration: 'underline' }}>📍 {it.address}</p>}
              {it.timeOfDay?.length > 0 && <div style={{ marginBottom: 8 }}>{it.timeOfDay.map(t => <span key={t} style={{ fontSize: 11, backgroundColor: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: 10, marginRight: 6 }}>{t}</span>)}</div>}
              <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#4B5563', flexWrap: 'wrap', alignItems: 'center' }}>
                {it.mealType && <span style={{ backgroundColor: '#DB2777', color: 'white', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{it.mealType}</span>}
                {it.mealTime && <span style={{ fontWeight: 600, color: '#DB2777' }}>@ {it.mealTime}</span>}
                {it.cuisine && <span style={{ backgroundColor: '#FDF2F8', color: '#DB2777', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>{it.cuisine}</span>}
                {it.hours && <span>Hours: {it.hours}</span>}
                {(it.adults || it.students || it.infants) && <span>Party: {[it.adults && `${it.adults} adult(s)`, it.students && `${it.students} student(s)`, it.infants && `${it.infants} infant(s)`].filter(Boolean).join(', ')}</span>}
                {it.venueType && <span>Type: {it.venueType}</span>}
              </div>
              {it.seating && <div style={{ marginTop: 8, fontSize: 13, color: '#4B5563' }}>Seating: {it.seating}</div>}
              {it.menu && <div style={{ marginTop: 4, fontSize: 13, color: '#4B5563' }}>Menu: {it.menu}</div>}
              {it.remarks && <div style={{ marginTop: 8, padding: 8, backgroundColor: '#EFF6FF', borderRadius: 6, fontSize: 13 }}>📝 {it.remarks}</div>}
            </div>
          );
        })}

        {allEvents.length === 0 && !adding && (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No activities yet</div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>← Back</button>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Itinerary</h2>
        </div>
        <button onClick={() => generatePrintableItinerary(trip, hotels)} style={{ padding: '8px 16px', backgroundColor: 'white', color: '#3B82F6', border: '1px solid #3B82F6', borderRadius: 8, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          🖨️ Print Itinerary
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16 }}>
        {trip?.days?.map(d => {
          const hotel = getTonightHotel(d.date);
          return (
            <div key={d.id} onClick={() => setSelectedDay(d.id)} style={{ backgroundColor: 'white', borderRadius: 16, padding: 20, cursor: 'pointer', border: '1px solid #E5E7EB' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#3B82F6' }}>Day {d.dayNumber}{d.city ? ` — ${d.city}` : ''}</span>
              <h4 style={{ margin: '4px 0 12px', fontSize: 16 }}>{new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</h4>
              {hotel && <div style={{ backgroundColor: '#EFF6FF', borderRadius: 6, padding: 6, marginBottom: 8, fontSize: 12 }}>🏨 {hotel.name}</div>}
              <div style={{ fontSize: 13, color: '#6B7280' }}>
                <p style={{ margin: 0 }}>📍 {d.destinations?.length || 0} destinations</p>
                <p style={{ margin: '4px 0 0' }}>🍽️ {d.dining?.length || 0} dining</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ========== HOTELS ==========
function Hotels({ hotels, onCreate, onUpdate, onDelete, onBack, notify, inp, btn, btn2, tripName }) {
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const openMaps = (address) => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');

  const startEdit = (hotel) => {
    setForm({ ...hotel });
    setEditing(hotel.id);
  };

  const cancelEdit = () => {
    setForm({});
    setEditing(null);
  };

  const save = () => {
    if (!form.name) { notify('Name required', 'error'); return; }
    if (editing) {
      const { id, tripId, createdAt, ...data } = form;
      onUpdate(editing, data);
      setEditing(null);
    } else {
      onCreate(form);
    }
    setForm({});
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>← Back</button>
        <div><h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Trip Hotels</h2><p style={{ fontSize: 12, color: '#3B82F6', margin: 0 }}>{tripName}</p></div>
      </div>
      <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>{editing ? 'Edit Hotel' : 'Add Hotel'}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Hotel Name *" style={inp} />
          <input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address (links to Google Maps)" style={inp} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>Check-in Date</label><input type="date" value={form.startDate || ''} onChange={e => setForm({ ...form, startDate: e.target.value })} style={inp} /></div>
            <div><label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>Check-out Date</label><input type="date" value={form.endDate || ''} onChange={e => setForm({ ...form, endDate: e.target.value })} style={inp} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <input value={form.checkIn || ''} onChange={e => setForm({ ...form, checkIn: e.target.value })} placeholder="Check-in Time (e.g., 3:00 PM)" style={inp} />
            <input value={form.checkOut || ''} onChange={e => setForm({ ...form, checkOut: e.target.value })} placeholder="Check-out Time (e.g., 11:00 AM)" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <input type="number" value={form.roomsReserved || ''} onChange={e => setForm({ ...form, roomsReserved: e.target.value })} placeholder="Rooms Reserved" style={inp} />
            <input value={form.breakfastHours || ''} onChange={e => setForm({ ...form, breakfastHours: e.target.value })} placeholder="Breakfast Hours (e.g., 7-10 AM)" style={inp} />
          </div>
          <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Additional Notes" style={{ ...inp, minHeight: 60 }} />
          <div style={{ display: 'flex', gap: 12 }}>
            {editing && <button onClick={cancelEdit} style={{ ...btn2, flex: 1 }}>Cancel</button>}
            <button onClick={save} style={{ ...btn, flex: editing ? 1 : undefined }}>{editing ? 'Save Changes' : 'Add Hotel'}</button>
          </div>
        </div>
      </div>
      {hotels.map(h => (
        <div key={h.id} style={{ backgroundColor: '#EFF6FF', borderRadius: 16, padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>🏨 {h.name}</h4>
              {h.address && <p onClick={() => openMaps(h.address)} style={{ margin: '8px 0 0', fontSize: 14, color: '#0369A1', cursor: 'pointer', textDecoration: 'underline' }}>📍 {h.address} ↗</p>}
              {h.startDate && h.endDate && <p style={{ margin: '8px 0 0', fontSize: 14, color: '#1E40AF', fontWeight: 500 }}>📅 {new Date(h.startDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} – {new Date(h.endDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
                {h.checkIn && <span style={{ fontSize: 13, color: '#4B5563' }}>🕐 Check-in: {h.checkIn}</span>}
                {h.checkOut && <span style={{ fontSize: 13, color: '#4B5563' }}>🕐 Check-out: {h.checkOut}</span>}
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6 }}>
                {h.roomsReserved && <span style={{ fontSize: 13, color: '#4B5563' }}>🛏️ {h.roomsReserved} room(s)</span>}
                {h.breakfastHours && <span style={{ fontSize: 13, color: '#4B5563' }}>🍳 Breakfast: {h.breakfastHours}</span>}
              </div>
              {h.notes && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#6B7280', backgroundColor: 'white', padding: 10, borderRadius: 8 }}>📝 {h.notes}</p>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button onClick={() => startEdit(h)} style={{ background: 'none', border: 'none', color: '#0369A1', cursor: 'pointer', fontSize: 12 }}>Edit</button>
              <button onClick={() => onDelete(h.id)} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer', fontSize: 12 }}>Delete</button>
            </div>
          </div>
        </div>
      ))}
      {hotels.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No hotels added yet</div>}
    </div>
  );
}

// ========== FAMILIES ==========
function Families({ trip, families, invitations, memberDetails, tripHotels, onCreateFamily, onUpdateFamily, onDeleteFamily, onCreateInvitation, onUpdateInvitation, onDeleteInvitation, onAddFlight, onUpdateFlight, onDeleteFlight, onAddHotel, onUpdateHotel, onDeleteHotel, onUpdateLuggageTag, onBack, notify, inp, btn, btn2 }) {
  const [familyName, setFamilyName] = useState('');
  const [invForm, setInvForm] = useState({ name: '', email: '', code: '', age: 'Adult', dietaryPreferences: '', memberType: 'insider' });
  const [addingTo, setAddingTo] = useState(null);
  const [viewMember, setViewMember] = useState(null);
  const [flightForm, setFlightForm] = useState({});
  const [hotelForm, setHotelForm] = useState({});
  const [addingFlight, setAddingFlight] = useState(false);
  const [editingFlight, setEditingFlight] = useState(null);
  const [addingHotelForm, setAddingHotelForm] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [editingFamily, setEditingFamily] = useState(null);
  const [editFamilyName, setEditFamilyName] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({ name: '', email: '', isPrivate: false, age: 'Adult', dietaryPreferences: '', memberType: 'insider' });

  const copy = (t) => { navigator.clipboard.writeText(t); notify('Copied!'); };

  const startEditFlight = (flight) => {
    setFlightForm({ ...flight });
    setEditingFlight(flight.id);
    setAddingFlight(true);
  };

  const startEditHotel = (hotel) => {
    setHotelForm({ ...hotel });
    setEditingHotel(hotel.id);
    setAddingHotelForm(true);
  };

  const saveFlight = (inv) => {
    if (!flightForm.flightNumber) { notify('Flight # required', 'error'); return; }
    if (editingFlight) {
      const { id, ...data } = flightForm;
      onUpdateFlight(viewMember, editingFlight, data);
      setEditingFlight(null);
    } else {
      onAddFlight(viewMember, inv?.familyId, flightForm);
    }
    setFlightForm({});
    setAddingFlight(false);
  };

  const saveHotel = (inv) => {
    if (!hotelForm.hotelName && !hotelForm.tripHotelId) { notify('Select hotel', 'error'); return; }
    if (editingHotel) {
      const { id, ...data } = hotelForm;
      onUpdateHotel(viewMember, editingHotel, data);
      setEditingHotel(null);
    } else {
      onAddHotel(viewMember, inv?.familyId, hotelForm);
    }
    setHotelForm({});
    setAddingHotelForm(false);
  };

  // Member detail view
  if (viewMember) {
    const inv = invitations.find(i => i.id === viewMember);
    const details = memberDetails.find(m => m.invitationId === viewMember) || { flights: [], hotels: [], luggageTag: null };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <button onClick={() => setViewMember(null)} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>← Back</button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{inv?.travelerName}</h2>
              <span style={{ fontSize: 10, backgroundColor: (inv?.memberType || 'insider') === 'insider' ? '#DBEAFE' : '#E0E7FF', color: (inv?.memberType || 'insider') === 'insider' ? '#1E40AF' : '#3730A3', padding: '2px 6px', borderRadius: 10 }}>{(inv?.memberType || 'insider') === 'insider' ? 'Insider' : 'Traveler'}</span>
              {inv?.age && inv.age !== 'Adult' && <span style={{ fontSize: 10, backgroundColor: '#EFF6FF', color: '#1E40AF', padding: '2px 6px', borderRadius: 10 }}>{inv.age}</span>}
            </div>
            <p style={{ fontSize: 12, color: '#3B82F6', margin: '4px 0 0' }}>Travel Details{inv?.dietaryPreferences && <span style={{ marginLeft: 12, color: '#6B7280' }}>🍽️ {inv.dietaryPreferences}</span>}</p>
          </div>
        </div>

        {/* Flights */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#0369A1' }}>✈️ Flights</h3>
            {!addingFlight && <button onClick={() => { setAddingFlight(true); setEditingFlight(null); setFlightForm({}); }} style={{ ...btn, padding: '6px 12px', fontSize: 12, backgroundColor: '#0369A1' }}>+ Add</button>}
          </div>
          {addingFlight && (
            <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 16, border: '2px solid #0369A1', marginBottom: 12 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: '#0369A1', margin: '0 0 12px' }}>✈️ {editingFlight ? 'Edit' : 'Add'} Flight</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Flight Date</label><input value={flightForm.flightDate || ''} onChange={e => setFlightForm({ ...flightForm, flightDate: e.target.value })} placeholder="2026-02-13" style={inp} /></div>
                <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Flight Number *</label><input value={flightForm.flightNumber || ''} onChange={e => setFlightForm({ ...flightForm, flightNumber: e.target.value })} placeholder="SQ123" style={inp} /></div>
                <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Airline</label><input value={flightForm.airline || ''} onChange={e => setFlightForm({ ...flightForm, airline: e.target.value })} placeholder="Singapore Airlines" style={inp} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Departure Time</label><input value={flightForm.flightDepTime || ''} onChange={e => setFlightForm({ ...flightForm, flightDepTime: e.target.value })} placeholder="08:30" style={inp} /></div>
                  <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Arrival Time</label><input value={flightForm.flightArrTime || ''} onChange={e => setFlightForm({ ...flightForm, flightArrTime: e.target.value })} placeholder="14:45" style={inp} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>From Airport</label><input value={flightForm.depAirport || ''} onChange={e => setFlightForm({ ...flightForm, depAirport: e.target.value })} placeholder="LAX" style={inp} /></div>
                  <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>To Airport</label><input value={flightForm.arrAirport || ''} onChange={e => setFlightForm({ ...flightForm, arrAirport: e.target.value })} placeholder="NRT" style={inp} /></div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setAddingFlight(false); setEditingFlight(null); setFlightForm({}); }} style={{ ...btn2, flex: 1 }}>Cancel</button>
                  <button onClick={() => saveFlight(inv)} style={{ ...btn, flex: 1, backgroundColor: '#0369A1' }}>{editingFlight ? 'Save Changes' : 'Add Flight'}</button>
                </div>
              </div>
            </div>
          )}
          {details.flights?.map(f => (
            <div key={f.id} style={{ backgroundColor: '#F0F9FF', borderRadius: 8, padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{f.airline} {f.flightNumber}</div>
                  <div style={{ fontSize: 13, color: '#4B5563' }}>{f.depAirport} → {f.arrAirport}</div>
                  {f.flightDate && <div style={{ fontSize: 12, color: '#6B7280' }}>{f.flightDate} {f.flightDepTime && `@ ${f.flightDepTime}`}{f.flightArrTime && ` - ${f.flightArrTime}`}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => startEditFlight(f)} style={{ background: 'none', border: 'none', color: '#0369A1', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                  <button onClick={() => onDeleteFlight(viewMember, f.id)} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer' }}>×</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Hotels */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#1E40AF' }}>🏨 Room Assignments</h3>
            {!addingHotelForm && <button onClick={() => { setAddingHotelForm(true); setEditingHotel(null); setHotelForm({}); }} style={{ ...btn, padding: '6px 12px', fontSize: 12, backgroundColor: '#1E40AF' }}>+ Add</button>}
          </div>
          {addingHotelForm && (
            <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 16, border: '2px solid #1E40AF', marginBottom: 12 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1E40AF', margin: '0 0 12px' }}>🏨 {editingHotel ? 'Edit' : 'Add'} Room Assignment</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tripHotels.length > 0 ? (
                  <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Select Hotel *</label><select value={hotelForm.tripHotelId || ''} onChange={e => { const h = tripHotels.find(x => x.id === e.target.value); setHotelForm({ ...hotelForm, tripHotelId: e.target.value, hotelName: h?.name }); }} style={{ ...inp, cursor: 'pointer' }}><option value="">-- Select a hotel --</option>{tripHotels.map(h => <option key={h.id} value={h.id}>{h.name} ({h.startDate && h.endDate ? `${new Date(h.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(h.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'No dates'})</option>)}</select></div>
                ) : (
                  <div style={{ padding: 12, backgroundColor: '#EFF6FF', borderRadius: 8, fontSize: 13, color: '#1E40AF' }}>⚠️ No trip hotels added yet. Add hotels in Hotels section first.</div>
                )}
                <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Room Type</label><input value={hotelForm.roomType || ''} onChange={e => setHotelForm({ ...hotelForm, roomType: e.target.value })} placeholder="Deluxe King" style={inp} /></div>
                <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Confirmation #</label><input value={hotelForm.hotelConfirm || ''} onChange={e => setHotelForm({ ...hotelForm, hotelConfirm: e.target.value })} style={inp} /></div>
                <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Guests in Room</label><input value={hotelForm.hotelGuests || ''} onChange={e => setHotelForm({ ...hotelForm, hotelGuests: e.target.value })} placeholder="2" style={inp} /></div>
                <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Sharing Room With</label><input value={hotelForm.sharingWith || ''} onChange={e => setHotelForm({ ...hotelForm, sharingWith: e.target.value })} placeholder="Family member names" style={inp} /></div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={hotelForm.breakfastIncluded || false} onChange={e => setHotelForm({ ...hotelForm, breakfastIncluded: e.target.checked })} />
                  <span style={{ fontSize: 13, color: '#374151' }}>Breakfast Included</span>
                </label>
                <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Remarks</label><textarea value={hotelForm.hotelRemarks || ''} onChange={e => setHotelForm({ ...hotelForm, hotelRemarks: e.target.value })} placeholder="Special requests..." style={{ ...inp, minHeight: 60 }} /></div>
                <div><label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4 }}>Confirmation Document</label>{hotelForm.document ? (<div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, backgroundColor: '#F0FDF4', borderRadius: 6, border: '1px solid #BBF7D0' }}><span style={{ fontSize: 20 }}>{hotelForm.documentType?.includes('pdf') ? '📄' : '🖼️'}</span><span style={{ flex: 1, fontSize: 13, color: '#166534' }}>{hotelForm.documentName}</span><button onClick={() => setHotelForm({ ...hotelForm, document: null, documentName: null, documentType: null })} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer', fontSize: 12 }}>Remove</button></div>) : (<label style={{ display: 'block', border: '2px dashed #D1D5DB', borderRadius: 8, padding: 16, textAlign: 'center', cursor: 'pointer', backgroundColor: '#FAFAFA' }}><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => { const f = e.target.files?.[0]; if (f) { if (f.size > 10485760) { notify('Max 10MB', 'error'); return; } const reader = new FileReader(); reader.onload = ev => setHotelForm({ ...hotelForm, document: ev.target.result, documentName: f.name, documentType: f.type }); reader.readAsDataURL(f); }}} style={{ display: 'none' }} /><span style={{ fontSize: 24, display: 'block', marginBottom: 4 }}>📎</span><span style={{ fontSize: 12, color: '#6B7280' }}>Attach PDF, JPG, or PNG (max 10MB)</span></label>)}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setAddingHotelForm(false); setEditingHotel(null); setHotelForm({}); }} style={{ ...btn2, flex: 1 }}>Cancel</button>
                  <button onClick={() => saveHotel(inv)} style={{ ...btn, flex: 1, backgroundColor: '#1E40AF' }}>{editingHotel ? 'Save Changes' : 'Add Room'}</button>
                </div>
              </div>
            </div>
          )}
          {details.hotels?.map(h => (
            <div key={h.id} style={{ backgroundColor: '#EFF6FF', borderRadius: 8, padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{h.hotelName}</div>
                  {h.roomType && <div style={{ fontSize: 13, color: '#4B5563' }}>Room: {h.roomType}</div>}
                  {h.hotelConfirm && <div style={{ fontSize: 12, color: '#6B7280' }}>Conf #: {h.hotelConfirm}</div>}
                  {h.hotelGuests && <div style={{ fontSize: 12, color: '#6B7280' }}>Guests: {h.hotelGuests}</div>}
                  {h.sharingWith && <div style={{ fontSize: 12, color: '#6B7280' }}>Sharing: {h.sharingWith}</div>}
                  {h.breakfastIncluded && <div style={{ fontSize: 12, color: '#166534', marginTop: 4 }}>🍳 Breakfast Included</div>}
                  {h.hotelRemarks && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>📝 {h.hotelRemarks}</div>}
                  {h.document && (<div style={{ marginTop: 8 }}><a href={h.document} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', backgroundColor: 'white', border: '1px solid #60A5FA', borderRadius: 6, fontSize: 11, color: '#1E40AF', textDecoration: 'none' }}><span>{h.documentType?.includes('pdf') ? '📄' : '🖼️'}</span><span>{h.documentName || 'View Document'}</span></a></div>)}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => startEditHotel(h)} style={{ background: 'none', border: 'none', color: '#0369A1', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                  <button onClick={() => onDeleteHotel(viewMember, h.id)} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer' }}>×</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Luggage Tags */}
        <div style={{ marginTop: 32 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#7C3AED' }}>🏷️ Luggage Tags</h3>
          {details.luggageTag ? (
            <div style={{ backgroundColor: '#F5F3FF', borderRadius: 12, padding: 16 }}>
              <img
                src={details.luggageTag}
                alt="Luggage Tag"
                style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, marginBottom: 12 }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none', padding: 16, backgroundColor: '#FEF3C7', borderRadius: 8, marginBottom: 12, color: '#92400E', fontSize: 13 }}>
                ⚠️ Image format not supported. Please remove and upload a different image (JPEG or PNG recommended).
              </div>
              <button onClick={() => onUpdateLuggageTag(viewMember, inv?.familyId, null)} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer', fontSize: 12 }}>Remove Image</button>
            </div>
          ) : (
            <label style={{ display: 'block', border: '2px dashed #C4B5FD', borderRadius: 12, padding: 24, textAlign: 'center', cursor: 'pointer', backgroundColor: '#F5F3FF' }}>
              <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={async e => {
                const f = e.target.files?.[0];
                if (f) {
                  if (f.size > 5242880) { notify('Max 5MB for luggage tag image', 'error'); return; }
                  if (!f.type.match(/^image\/(jpeg|png)$/)) { notify('Only JPEG or PNG allowed', 'error'); return; }
                  try {
                    const compressedBase64 = await compressImage(f, 800, 0.8);
                    if (!compressedBase64.startsWith('data:image')) {
                      notify('Invalid image format. Please try a different image.', 'error');
                      return;
                    }
                    onUpdateLuggageTag(viewMember, inv?.familyId, compressedBase64);
                  } catch (err) {
                    console.error('[Upload] Compression failed:', err);
                    notify('Failed to process image. Please try a different image.', 'error');
                  }
                }
              }} style={{ display: 'none' }} />
              <span style={{ fontSize: 32, display: 'block', marginBottom: 8 }}>🏷️</span>
              <span style={{ fontSize: 13, color: '#7C3AED', fontWeight: 500 }}>Upload Luggage Tag Image</span>
              <span style={{ fontSize: 11, color: '#9CA3AF', display: 'block', marginTop: 4 }}>JPEG or PNG, max 5MB (will be compressed)</span>
            </label>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>← Back</button>
        <div><h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Families</h2><p style={{ fontSize: 12, color: '#3B82F6', margin: 0 }}>{trip?.name}</p></div>
      </div>

      {/* Add Family */}
      <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <input value={familyName} onChange={e => setFamilyName(e.target.value)} placeholder="Family Name" style={{ ...inp, flex: 1 }} />
          <button onClick={() => { if (!familyName) { notify('Enter name', 'error'); return; } onCreateFamily(familyName); setFamilyName(''); }} style={btn}>Add</button>
        </div>
      </div>

      {/* Family List */}
      {families.map(f => {
        const fInvs = invitations.filter(i => i.familyId === f.id);
        const isEditingThisFamily = editingFamily === f.id;
        return (
          <div key={f.id} style={{ backgroundColor: 'white', borderRadius: 16, padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              {isEditingThisFamily ? (
                <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                  <input value={editFamilyName} onChange={e => setEditFamilyName(e.target.value)} style={{ ...inp, flex: 1 }} />
                  <button onClick={() => { setEditingFamily(null); setEditFamilyName(''); }} style={{ ...btn2, padding: '8px 12px' }}>Cancel</button>
                  <button onClick={() => { if (!editFamilyName) { notify('Enter name', 'error'); return; } onUpdateFamily(f.id, editFamilyName); setEditingFamily(null); setEditFamilyName(''); }} style={{ ...btn, padding: '8px 12px' }}>Save</button>
                </div>
              ) : (
                <>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{f.name}</h3>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setEditingFamily(f.id); setEditFamilyName(f.name); }} style={{ background: 'none', border: 'none', color: '#0369A1', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                    <button onClick={() => onDeleteFamily(f.id)} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                  </div>
                </>
              )}
            </div>
            {fInvs.map(inv => {
              const isEditingThisMember = editingMember === inv.id;
              return (
                <div key={inv.id} style={{ padding: 12, backgroundColor: inv.used ? '#F0FDF4' : '#DBEAFE', borderRadius: 8, marginBottom: 8 }}>
                  {isEditingThisMember ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input type="radio" name={`memberType-${inv.id}`} checked={editMemberForm.memberType === 'insider'} onChange={() => setEditMemberForm({ ...editMemberForm, memberType: 'insider' })} />
                          <span style={{ fontSize: 13 }}>Insider</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input type="radio" name={`memberType-${inv.id}`} checked={editMemberForm.memberType === 'traveler'} onChange={() => setEditMemberForm({ ...editMemberForm, memberType: 'traveler' })} />
                          <span style={{ fontSize: 13 }}>Traveler</span>
                        </label>
                      </div>
                      <input value={editMemberForm.name} onChange={e => setEditMemberForm({ ...editMemberForm, name: e.target.value })} placeholder="Name *" style={inp} />
                      {editMemberForm.memberType === 'insider' && (
                        <input value={editMemberForm.email} onChange={e => setEditMemberForm({ ...editMemberForm, email: e.target.value })} placeholder="Email *" style={inp} type="email" />
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 2 }}>Age Group</label>
                          <select value={editMemberForm.age} onChange={e => setEditMemberForm({ ...editMemberForm, age: e.target.value })} style={{ ...inp, width: '100%' }}>
                            <option value="Adult">Adult</option>
                            <option value="Student">Student</option>
                            <option value="Infant">Infant</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 2 }}>Dietary Preferences</label>
                          <input value={editMemberForm.dietaryPreferences} onChange={e => setEditMemberForm({ ...editMemberForm, dietaryPreferences: e.target.value })} placeholder="e.g., Vegetarian" style={inp} />
                        </div>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={editMemberForm.isPrivate || false} onChange={e => setEditMemberForm({ ...editMemberForm, isPrivate: e.target.checked })} />
                        <span style={{ fontSize: 13, color: '#374151' }}>Private (hide details from other family members)</span>
                      </label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => { setEditingMember(null); setEditMemberForm({ name: '', email: '', isPrivate: false, age: 'Adult', dietaryPreferences: '', memberType: 'insider' }); }} style={{ ...btn2, flex: 1 }}>Cancel</button>
                        <button onClick={() => {
                          if (!editMemberForm.name) { notify('Name required', 'error'); return; }
                          if (editMemberForm.memberType === 'insider' && !editMemberForm.email) { notify('Email required for Insider', 'error'); return; }
                          onUpdateInvitation(inv.id, { travelerName: editMemberForm.name, email: editMemberForm.memberType === 'traveler' ? '' : editMemberForm.email, isPrivate: editMemberForm.isPrivate, age: editMemberForm.age, dietaryPreferences: editMemberForm.dietaryPreferences, memberType: editMemberForm.memberType });
                          setEditingMember(null);
                          setEditMemberForm({ name: '', email: '', isPrivate: false, age: 'Adult', dietaryPreferences: '', memberType: 'insider' });
                        }} style={{ ...btn, flex: 1 }}>Save</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                          <span style={{ fontWeight: 600 }}>{inv.travelerName}</span>
                          <span style={{ fontSize: 10, backgroundColor: (inv.memberType || 'insider') === 'insider' ? '#DBEAFE' : '#E0E7FF', color: (inv.memberType || 'insider') === 'insider' ? '#1E40AF' : '#3730A3', padding: '2px 6px', borderRadius: 10 }}>{(inv.memberType || 'insider') === 'insider' ? 'Insider' : 'Traveler'}</span>
                          <span style={{ fontSize: 10, backgroundColor: '#F3F4F6', color: '#4B5563', padding: '2px 6px', borderRadius: 10 }}>{inv.age || 'Adult'}</span>
                          {inv.isPrivate && <span style={{ fontSize: 10, backgroundColor: '#E5E7EB', color: '#4B5563', padding: '2px 6px', borderRadius: 10 }}>Private</span>}
                        </div>
                        <div style={{ marginTop: 4, fontSize: 12, color: '#6B7280' }}>
                          {inv.email && <span>{inv.email}</span>}
                          {inv.dietaryPreferences && <span style={{ marginLeft: inv.email ? 12 : 0 }}>🍽️ {inv.dietaryPreferences}</span>}
                        </div>
                        {(inv.memberType || 'insider') === 'insider' && !inv.used && <span onClick={() => copy(inv.code)} style={{ fontFamily: 'monospace', backgroundColor: '#EFF6FF', padding: '2px 6px', borderRadius: 4, fontSize: 11, cursor: 'pointer', color: '#1E40AF', display: 'inline-block', marginTop: 4 }}>{inv.code} 📋</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {(inv.memberType || 'insider') === 'insider' && (inv.used ? <span style={{ color: '#166534', fontSize: 12 }}>✓ Joined</span> : <span style={{ color: '#1E40AF', fontSize: 12 }}>Pending</span>)}
                        <button onClick={() => { setEditingMember(inv.id); setEditMemberForm({ name: inv.travelerName, email: inv.email, isPrivate: inv.isPrivate || false, age: inv.age || 'Adult', dietaryPreferences: inv.dietaryPreferences || '', memberType: inv.memberType || 'insider' }); }} style={{ background: 'none', border: 'none', color: '#0369A1', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                        <button onClick={() => setViewMember(inv.id)} style={{ background: 'none', border: 'none', color: '#0369A1', cursor: 'pointer', fontSize: 12 }}>Details</button>
                        <button onClick={() => onDeleteInvitation(inv.id)} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer' }}>×</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {addingTo === f.id ? (
              <div style={{ backgroundColor: '#F9FAFB', borderRadius: 8, padding: 12, marginTop: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="radio" name="memberType" checked={invForm.memberType === 'insider'} onChange={() => setInvForm({ ...invForm, memberType: 'insider' })} />
                      <span style={{ fontSize: 13 }}>Insider</span>
                      <span style={{ fontSize: 10, color: '#6B7280' }}>(app access)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="radio" name="memberType" checked={invForm.memberType === 'traveler'} onChange={() => setInvForm({ ...invForm, memberType: 'traveler' })} />
                      <span style={{ fontSize: 13 }}>Traveler</span>
                      <span style={{ fontSize: 10, color: '#6B7280' }}>(no login)</span>
                    </label>
                  </div>
                  <input value={invForm.name} onChange={e => setInvForm({ ...invForm, name: e.target.value })} placeholder="Name *" style={inp} />
                  {invForm.memberType === 'insider' && (
                    <>
                      <input value={invForm.email} onChange={e => setInvForm({ ...invForm, email: e.target.value })} placeholder="Email *" style={inp} type="email" />
                      <input value={invForm.code} onChange={e => setInvForm({ ...invForm, code: e.target.value.toUpperCase() })} placeholder="Custom Code (opt)" style={{ ...inp, fontFamily: 'monospace' }} />
                    </>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 2 }}>Age Group</label>
                      <select value={invForm.age} onChange={e => setInvForm({ ...invForm, age: e.target.value })} style={{ ...inp, width: '100%' }}>
                        <option value="Adult">Adult</option>
                        <option value="Student">Student</option>
                        <option value="Infant">Infant</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 2 }}>Dietary Preferences</label>
                      <input value={invForm.dietaryPreferences} onChange={e => setInvForm({ ...invForm, dietaryPreferences: e.target.value })} placeholder="e.g., Vegetarian" style={inp} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setAddingTo(null); setInvForm({ name: '', email: '', code: '', age: 'Adult', dietaryPreferences: '', memberType: 'insider' }); }} style={{ ...btn2, flex: 1 }}>Cancel</button>
                    <button onClick={() => {
                      if (!invForm.name) { notify('Name required', 'error'); return; }
                      if (invForm.memberType === 'insider' && !invForm.email) { notify('Email required for Insider', 'error'); return; }
                      onCreateInvitation(f.id, invForm.name, invForm.memberType === 'traveler' ? '' : invForm.email, invForm.code, invForm.age, invForm.dietaryPreferences, invForm.memberType);
                      setInvForm({ name: '', email: '', code: '', age: 'Adult', dietaryPreferences: '', memberType: 'insider' });
                      setAddingTo(null);
                    }} style={{ ...btn, flex: 1 }}>Add</button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingTo(f.id)} style={{ width: '100%', padding: 10, backgroundColor: '#F3F4F6', border: '1px dashed #D1D5DB', borderRadius: 8, color: '#6B7280', cursor: 'pointer', marginTop: 8 }}>+ Add Member</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ========== ANNOUNCEMENTS ==========
function Announcements({ announcements, onCreate, onDelete, onBack, notify, inp, btn, btn2, tripName }) {
  const [form, setForm] = useState({ title: '', message: '', date: '', city: '' });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>← Back</button>
        <div><h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Announcements</h2><p style={{ fontSize: 12, color: '#3B82F6', margin: 0 }}>{tripName}</p></div>
      </div>
      <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Title *" style={inp} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 4 }}>City</label>
              <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="e.g., Kyoto" style={inp} />
            </div>
          </div>
          <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Message *" style={{ ...inp, minHeight: 80 }} />
          <button onClick={() => { if (!form.title || !form.message) { notify('Title & message required', 'error'); return; } onCreate({ title: form.title, message: form.message, date: form.date, city: form.city }); setForm({ title: '', message: '', date: '', city: '' }); }} style={btn}>Post</button>
        </div>
      </div>
      {announcements.map(a => (
        <div key={a.id} style={{ backgroundColor: '#DBEAFE', borderRadius: 16, padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{a.title}</h4>
              {(a.date || a.city) && (
                <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 12, color: '#1E40AF' }}>
                  {a.date && <span>📅 {new Date(a.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
                  {a.city && <span>📍 {a.city}</span>}
                </div>
              )}
            </div>
            <button onClick={() => onDelete(a.id)} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer' }}>Delete</button>
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: '#4B5563', whiteSpace: 'pre-wrap' }}>{a.message}</p>
        </div>
      ))}
    </div>
  );
}

// ========== MESSAGES (Admin View) ==========
function Messages({ messages, onAddToThread, onMarkRead, onDelete, onBack, notify, inp, btn, btn2, tripName }) {
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const sortedThreads = [...messages].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));

  // Helper to get thread messages (handles old format migration)
  const getThreadMessages = (thread) => {
    if (thread.messages) return thread.messages;
    // Migrate old format
    const msgs = [];
    if (thread.message) {
      msgs.push({ id: '1', sender: thread.senderName, senderRole: 'traveler', text: thread.message, timestamp: thread.createdAt });
    }
    if (thread.reply) {
      msgs.push({ id: '2', sender: 'Organizer', senderRole: 'admin', text: thread.reply, timestamp: thread.repliedAt });
    }
    return msgs;
  };

  const handleReply = (threadId) => {
    if (!replyText.trim()) { notify('Enter reply', 'error'); return; }
    onAddToThread(threadId, replyText);
    setReplyTo(null);
    setReplyText('');
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>← Back</button>
        <div><h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Messages</h2><p style={{ fontSize: 12, color: '#3B82F6', margin: 0 }}>{tripName}</p></div>
      </div>

      {sortedThreads.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, backgroundColor: 'white', borderRadius: 16, color: '#9CA3AF' }}>
          <p style={{ fontSize: 32, marginBottom: 8 }}>💬</p>
          <p>No messages yet</p>
          <p style={{ fontSize: 14 }}>Travelers can send you messages from their app</p>
        </div>
      )}

      {sortedThreads.map(thread => {
        const threadMsgs = getThreadMessages(thread);
        const threadFull = threadMsgs.length >= 10;
        const travelerName = thread.travelerName || thread.senderName || 'Unknown';

        return (
          <div key={thread.id} onClick={() => !thread.read && onMarkRead(thread.id)} style={{ backgroundColor: thread.read ? 'white' : '#EFF6FF', borderRadius: 16, padding: 20, marginBottom: 16, border: thread.read ? '1px solid #E5E7EB' : '2px solid #60A5FA' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#3B82F6', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 14 }}>{travelerName.charAt(0)}</span>
                <span style={{ fontWeight: 600 }}>{travelerName}</span>
                <span style={{ fontSize: 10, backgroundColor: '#DBEAFE', color: '#1E40AF', padding: '2px 6px', borderRadius: 10 }}>Insider</span>
                {!thread.read && <span style={{ fontSize: 10, backgroundColor: '#DC2626', color: 'white', padding: '2px 6px', borderRadius: 10 }}>NEW</span>}
                <span style={{ fontSize: 11, color: '#6B7280' }}>({threadMsgs.length}/10 messages)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>{new Date(thread.updatedAt || thread.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                <button onClick={(e) => { e.stopPropagation(); onDelete(thread.id); }} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer', fontSize: 12 }}>Delete</button>
              </div>
            </div>

            {/* Thread messages */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              {threadMsgs.map((msg, idx) => (
                <div key={msg.id || idx} style={{ backgroundColor: msg.senderRole === 'admin' ? '#ECFDF5' : '#F0F9FF', borderRadius: 8, padding: 12, borderLeft: `3px solid ${msg.senderRole === 'admin' ? '#047857' : '#0369A1'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: msg.senderRole === 'admin' ? '#047857' : '#0369A1' }}>{msg.senderRole === 'admin' ? 'You' : msg.sender}</span>
                    <span style={{ fontSize: 10, color: '#9CA3AF' }}>{new Date(msg.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 14, color: '#1F2937', whiteSpace: 'pre-wrap' }}>{msg.text}</p>
                </div>
              ))}
            </div>

            {threadFull ? (
              <div style={{ padding: 12, backgroundColor: '#FEE2E2', borderRadius: 8, textAlign: 'center', fontSize: 13, color: '#991B1B' }}>Thread limit reached (10 messages)</div>
            ) : replyTo === thread.id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..." style={{ ...inp, minHeight: 60 }} autoFocus />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setReplyTo(null); setReplyText(''); }} style={{ ...btn2, flex: 1, padding: '8px 16px' }}>Cancel</button>
                  <button onClick={() => handleReply(thread.id)} style={{ ...btn, flex: 1, padding: '8px 16px', backgroundColor: '#047857' }}>Send</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setReplyTo(thread.id)} style={{ ...btn, padding: '8px 16px', backgroundColor: '#047857' }}>Reply</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ========== TRAVELER DASHBOARD ==========
function TravelerDashboard({ user, myTrip, myDetails, myHotels, myAnns, myMsgs, myFamily, familyMembers, familyMemberDetails, onCreateThread, onAddToThread, logout, btn, inp, Notif, notify, isOffline, OfflineBanner }) {
  const [msgText, setMsgText] = useState('');
  const [expandedMember, setExpandedMember] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [expandedHotelDetail, setExpandedHotelDetail] = useState(null);
  const [expandedThread, setExpandedThread] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showConverter, setShowConverter] = useState(false);

  const getTonightHotel = (date) => myHotels?.find(h => h.startDate <= date && h.endDate > date);

  // Helper to get thread messages (handles old format)
  const getThreadMessages = (thread) => {
    if (thread.messages) return thread.messages;
    const msgs = [];
    if (thread.message) msgs.push({ id: '1', sender: thread.senderName, senderRole: 'traveler', text: thread.message, timestamp: thread.createdAt });
    if (thread.reply) msgs.push({ id: '2', sender: 'Organizer', senderRole: 'admin', text: thread.reply, timestamp: thread.repliedAt });
    return msgs;
  };

  const handleSend = () => {
    if (isOffline) { notify('Messaging unavailable offline', 'error'); return; }
    if (!msgText.trim()) { notify('Enter a message', 'error'); return; }
    onCreateThread(msgText);
    setMsgText('');
    notify('Message sent!');
  };

  const handleReply = (threadId) => {
    if (isOffline) { notify('Messaging unavailable offline', 'error'); return; }
    if (!replyText.trim()) { notify('Enter a message', 'error'); return; }
    onAddToThread(threadId, replyText);
    setReplyText('');
    setReplyingTo(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FAF8F5', fontFamily: 'Georgia, serif', paddingTop: isOffline ? 36 : 0 }}>
      <Notif />
      <OfflineBanner />
      {showConverter && <CurrencyConverter onClose={() => setShowConverter(false)} />}
      <header style={{ backgroundColor: 'white', borderBottom: '1px solid #E5E7EB', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 24, color: '#3B82F6' }}>✦</span>
          <div><h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{myTrip?.name || 'Trip Concierge'}</h1><p style={{ fontSize: 12, color: '#3B82F6', margin: 0, fontStyle: 'italic' }}>{myTrip?.destination}</p></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => setShowConverter(true)} style={{ padding: '6px 10px', backgroundColor: '#EFF6FF', color: '#3B82F6', border: '1px solid #3B82F6', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>💱</button>
          <span style={{ fontSize: 14 }}>{user?.name}</span>
          <button onClick={logout} style={{ padding: '6px 12px', backgroundColor: 'transparent', color: '#6B7280', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Sign Out</button>
        </div>
      </header>
      <main style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
        {myAnns.length > 0 && (
          <div style={{ backgroundColor: '#EFF6FF', borderRadius: 16, padding: 20, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>📢 Announcements</h3>
            {myAnns.map(a => (
              <div key={a.id} style={{ backgroundColor: 'white', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{a.title}</h4>
                {(a.date || a.city) && (
                  <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 12, color: '#1E40AF' }}>
                    {a.date && <span>📅 {new Date(a.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
                    {a.city && <span>📍 {a.city}</span>}
                  </div>
                )}
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#4B5563' }}>{a.message}</p>
              </div>
            ))}
          </div>
        )}
        {myTrip && (
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>📅 Itinerary</h3>
              <button onClick={() => generatePrintableItinerary(myTrip, myHotels, myDetails, user?.name)} style={{ padding: '6px 12px', backgroundColor: 'white', color: '#3B82F6', border: '1px solid #3B82F6', borderRadius: 6, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                🖨️ Print
              </button>
            </div>
            {myTrip.days?.map(d => {
              const isExpanded = expandedDay === d.id;
              const hotel = getTonightHotel(d.date);
              return (
                <div key={d.id} style={{ marginBottom: 8 }}>
                  <div
                    onClick={() => setExpandedDay(isExpanded ? null : d.id)}
                    style={{ backgroundColor: '#F9FAFB', padding: 12, borderRadius: isExpanded ? '8px 8px 0 0' : 8, cursor: 'pointer', border: '1px solid #E5E7EB' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 600, color: '#3B82F6' }}>Day {d.dayNumber}{d.city ? ` — ${d.city}` : ''}</span>
                        <span style={{ marginLeft: 12, color: '#6B7280', fontSize: 13 }}>{new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: '#6B7280' }}>{(d.destinations?.length || 0) + (d.dining?.length || 0)} activities</span>
                        <span style={{ color: '#6B7280' }}>{isExpanded ? '▼' : '▶'}</span>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (() => {
                    // Order: Morning destinations (1) → Lunch dining (2) → Afternoon destinations (3) → Dinner dining (4) → Evening destinations (5)
                    const getTimeValue = (item) => {
                      if (item.type === 'destination') {
                        const timeOfDay = item.timeOfDay?.[0];
                        if (timeOfDay === 'Morning') return 1 + (item.displayOrder || 1) * 0.001;
                        if (timeOfDay === 'Afternoon') return 3 + (item.displayOrder || 1) * 0.001;
                        if (timeOfDay === 'Evening') return 5 + (item.displayOrder || 1) * 0.001;
                        return 3 + (item.displayOrder || 1) * 0.001;
                      }
                      if (item.type === 'dining') {
                        if (item.mealType === 'Lunch') return 2;
                        if (item.mealType === 'Dinner') return 4;
                        if (item.mealTime) { const [h] = item.mealTime.split(':').map(Number); return h < 15 ? 2 : 4; }
                        return 3;
                      }
                      return 3;
                    };
                    const activities = [
                      ...(d.destinations?.map(dest => ({ ...dest, type: 'destination' })) || []),
                      ...(d.dining?.map(din => ({ ...din, type: 'dining' })) || [])
                    ].sort((a, b) => getTimeValue(a) - getTimeValue(b));

                    return (
                    <div style={{ backgroundColor: '#FAFAFA', padding: 16, borderRadius: '0 0 8px 8px', borderLeft: '1px solid #E5E7EB', borderRight: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}>
                      {/* Tonight's Hotel - Always First */}
                      {hotel && (
                        <div style={{ backgroundColor: '#EFF6FF', borderRadius: 8, padding: 12, marginBottom: 12, borderLeft: '3px solid #3B82F6' }}>
                          <div style={{ fontSize: 11, color: '#1E40AF', marginBottom: 4 }}>🏨 Tonight's Stay</div>
                          <div style={{ fontWeight: 600 }}>{hotel.name}</div>
                          {hotel.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(hotel.address)}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#4B5563', textDecoration: 'underline', display: 'block', marginTop: 2 }}>📍 {hotel.address}</a>}
                          {hotel.startDate && hotel.endDate && <div style={{ fontSize: 12, color: '#1E40AF', marginTop: 2 }}>📅 {new Date(hotel.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(hotel.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>}
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4, fontSize: 12, color: '#4B5563' }}>
                            {hotel.roomsReserved && <span>🛏️ {hotel.roomsReserved} room(s)</span>}
                            {hotel.breakfastHours && <span>🍳 Breakfast: {hotel.breakfastHours}</span>}
                          </div>
                        </div>
                      )}

                      {/* Combined Activities - Chronologically Sorted */}
                      {activities.map(item => item.type === 'destination' ? (
                        <div key={item.id} style={{ backgroundColor: '#ECFDF5', padding: 12, borderRadius: 8, marginBottom: 8, borderLeft: '3px solid #047857' }}>
                          <div style={{ fontWeight: 600, color: '#047857' }}>📍 {item.name}</div>
                          {item.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.address)}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#4B5563', textDecoration: 'underline', display: 'block', marginTop: 2 }}>📍 {item.address}</a>}
                          {item.timeOfDay?.length > 0 && <div style={{ marginTop: 4 }}>{item.timeOfDay.map(t => <span key={t} style={{ fontSize: 10, backgroundColor: '#D1FAE5', color: '#065F46', padding: '2px 6px', borderRadius: 10, marginRight: 4 }}>{t}</span>)}</div>}
                          {item.venueType && <div style={{ fontSize: 12, color: '#047857', marginTop: 4 }}>🏛️ {item.venueType}</div>}
                          {item.hours && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>🕐 Hours: {item.hours}</div>}
                          {item.remarks && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4, padding: 6, backgroundColor: '#F0FDF4', borderRadius: 4 }}>📝 {item.remarks}</div>}
                        </div>
                      ) : (
                        <div key={item.id} style={{ backgroundColor: '#FDF2F8', padding: 12, borderRadius: 8, marginBottom: 8, borderLeft: '3px solid #DB2777' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, color: '#DB2777' }}>🍽️ {item.name}</span>
                            {item.mealType && <span style={{ fontSize: 10, backgroundColor: '#DB2777', color: 'white', padding: '2px 6px', borderRadius: 10, fontWeight: 600 }}>{item.mealType}</span>}
                            {item.cuisine && <span style={{ fontSize: 10, backgroundColor: '#FCE7F3', color: '#DB2777', padding: '2px 6px', borderRadius: 10 }}>{item.cuisine}</span>}
                          </div>
                          {item.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.address)}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#4B5563', textDecoration: 'underline', display: 'block', marginTop: 2 }}>📍 {item.address}</a>}
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4, fontSize: 12, color: '#4B5563' }}>
                            {item.mealTime && <span style={{ fontWeight: 600, color: '#DB2777' }}>@ {item.mealTime}</span>}
                            {item.hours && <span>🕐 Hours: {item.hours}</span>}
                          </div>
                          {(item.adults || item.students || item.infants) && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>Party: {[item.adults && `${item.adults} adult(s)`, item.students && `${item.students} student(s)`, item.infants && `${item.infants} infant(s)`].filter(Boolean).join(', ')}</div>}
                          {item.seating && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Seating: {item.seating}</div>}
                          {item.menu && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Menu: {item.menu}</div>}
                          {item.remarks && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4, padding: 6, backgroundColor: '#FDF2F8', borderRadius: 4 }}>📝 {item.remarks}</div>}
                        </div>
                      ))}

                      {activities.length === 0 && (
                        <p style={{ color: '#9CA3AF', fontSize: 13, fontStyle: 'italic', textAlign: 'center', padding: 16 }}>No activities planned yet</p>
                      )}
                    </div>
                  );})()}
                </div>
              );
            })}
          </div>
        )}
        {myHotels.length > 0 && (
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>🏨 Hotels</h3>
            {myHotels.map(h => (
              <div key={h.id} style={{ backgroundColor: '#EFF6FF', padding: 16, borderRadius: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 16 }}>🏨 {h.name}</div>
                {h.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(h.address)}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#4B5563', textDecoration: 'underline', display: 'block', marginTop: 4 }}>📍 {h.address}</a>}
                {h.startDate && h.endDate && <div style={{ fontSize: 13, color: '#1E40AF', marginTop: 6 }}>📅 {new Date(h.startDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} – {new Date(h.endDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 13, color: '#4B5563' }}>
                  {h.checkIn && <span>🕐 Check-in: {h.checkIn}</span>}
                  {h.checkOut && <span>🕐 Check-out: {h.checkOut}</span>}
                </div>
                {h.breakfastHours && <div style={{ fontSize: 13, color: '#4B5563', marginTop: 4 }}>🍳 Breakfast: {h.breakfastHours}</div>}
              </div>
            ))}
          </div>
        )}
        {(myDetails.flights?.length > 0 || myDetails.hotels?.length > 0 || myDetails.luggageTag) && (
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>✈️ My Travel Details</h3>

            {/* Flights */}
            {myDetails.flights?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#0369A1', margin: '0 0 8px' }}>Flights</h4>
                {myDetails.flights.map(f => (
                  <div key={f.id} style={{ backgroundColor: '#F0F9FF', padding: 12, borderRadius: 8, marginBottom: 8, borderLeft: '3px solid #0369A1' }}>
                    <div style={{ fontWeight: 600 }}>{f.airline} {f.flightNumber}</div>
                    <div style={{ fontSize: 13, color: '#4B5563', marginTop: 2 }}>{f.depAirport} → {f.arrAirport}</div>
                    {f.flightDate && (
                      <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                        📅 {f.flightDate}
                        {(f.flightDepTime || f.flightArrTime) && (
                          <span style={{ marginLeft: 8 }}>
                            🕐 {f.flightDepTime || '—'} → {f.flightArrTime || '—'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Hotels/Room Assignments */}
            {myDetails.hotels?.length > 0 && (
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1E40AF', margin: '0 0 8px' }}>Room Assignments</h4>
                {myDetails.hotels.map(h => {
                  const isExpanded = expandedHotelDetail === h.id;
                  return (
                    <div key={h.id} style={{ backgroundColor: '#EFF6FF', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                      <div
                        onClick={() => setExpandedHotelDetail(isExpanded ? null : h.id)}
                        style={{ padding: 12, cursor: 'pointer', borderLeft: '3px solid #1E40AF' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{h.hotelName}</div>
                            {h.roomType && <div style={{ fontSize: 13, color: '#4B5563' }}>Room: {h.roomType}</div>}
                            {h.hotelConfirm && <div style={{ fontSize: 12, color: '#6B7280' }}>Conf #: {h.hotelConfirm}</div>}
                            {h.sharingWith && <div style={{ fontSize: 12, color: '#6B7280' }}>Sharing: {h.sharingWith}</div>}
                          </div>
                          <span style={{ color: '#6B7280', fontSize: 12 }}>{isExpanded ? '▼' : '▶'}</span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ padding: '0 12px 12px 12px', backgroundColor: '#DBEAFE' }}>
                          <div style={{ borderTop: '1px solid #60A5FA', paddingTop: 12 }}>
                            {h.hotelGuests && <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 4 }}>👥 Guests: {h.hotelGuests}</div>}
                            <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 4 }}>🍳 Breakfast: {h.breakfastIncluded ? <span style={{ color: '#166534' }}>Yes, included</span> : <span style={{ color: '#6B7280' }}>Not included</span>}</div>
                            {h.hotelRemarks && <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 8, padding: 8, backgroundColor: '#EFF6FF', borderRadius: 4 }}>📝 {h.hotelRemarks}</div>}
                            {h.document && (
                              <a href={h.document} download={h.documentName || 'confirmation'} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', backgroundColor: '#1E40AF', color: 'white', borderRadius: 6, fontSize: 12, textDecoration: 'none', fontWeight: 500 }}>
                                <span>📥</span> Download Confirmation
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Luggage Tag */}
            {myDetails.luggageTag && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#7C3AED', margin: '0 0 8px' }}>🏷️ My Luggage Tag</h4>
                <div style={{ backgroundColor: '#F5F3FF', padding: 12, borderRadius: 8 }}>
                  <img
                    src={myDetails.luggageTag}
                    alt="My Luggage Tag"
                    style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8 }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div style={{ display: 'none', padding: 12, backgroundColor: '#FEF3C7', borderRadius: 8, color: '#92400E', fontSize: 13 }}>
                    ⚠️ Image format not supported. Please contact the organizer to upload a different image.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Family Section */}
        {myFamily && familyMembers.length > 0 && (
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>👨‍👩‍👧‍👦 {myFamily.name}</h3>
            {familyMembers.map(member => {
              const isMe = member.id === user?.invitationId;
              const isExpanded = expandedMember === member.id;
              const memberDetail = familyMemberDetails.find(d => d.invitationId === member.id) || { flights: [], hotels: [], luggageTag: null };
              const isPrivate = member.isPrivate && !isMe;
              const canExpand = !isMe && !isPrivate;

              return (
                <div key={member.id} style={{ marginBottom: 8 }}>
                  <div
                    onClick={() => canExpand && setExpandedMember(isExpanded ? null : member.id)}
                    style={{
                      backgroundColor: isMe ? '#F0FDF4' : '#F9FAFB',
                      padding: 12,
                      borderRadius: isExpanded && canExpand ? '8px 8px 0 0' : 8,
                      cursor: canExpand ? 'pointer' : 'default',
                      border: isMe ? '2px solid #86EFAC' : '1px solid #E5E7EB'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600 }}>{member.travelerName}</span>
                          {isMe && <span style={{ fontSize: 10, backgroundColor: '#86EFAC', color: '#166534', padding: '2px 6px', borderRadius: 10 }}>You</span>}
                          <span style={{ fontSize: 10, backgroundColor: '#F3F4F6', color: '#4B5563', padding: '2px 6px', borderRadius: 10 }}>{member.age || 'Adult'}</span>
                          {isPrivate && <span style={{ fontSize: 11, color: '#9CA3AF' }}>(Private)</span>}
                        </div>
                        {(isMe || !isPrivate) && member.dietaryPreferences && (
                          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>🍽️ {member.dietaryPreferences}</div>
                        )}
                      </div>
                      {canExpand && <span style={{ fontSize: 12, color: '#6B7280' }}>{isExpanded ? '▼' : '▶'}</span>}
                    </div>
                  </div>

                  {isExpanded && canExpand && (
                    <div style={{ backgroundColor: '#FAFAFA', borderRadius: '0 0 8px 8px', padding: 16, marginTop: -4, borderLeft: '1px solid #E5E7EB', borderRight: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}>
                      {/* Flights */}
                      {memberDetail.flights?.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#0369A1', marginBottom: 6 }}>✈️ Flights</div>
                          {memberDetail.flights.map(f => (
                            <div key={f.id} style={{ backgroundColor: '#F0F9FF', padding: 8, borderRadius: 6, marginBottom: 4, fontSize: 13 }}>
                              <div style={{ fontWeight: 500 }}>{f.airline} {f.flightNumber}</div>
                              <div style={{ color: '#4B5563' }}>{f.depAirport} → {f.arrAirport}</div>
                              {f.flightDate && <div style={{ fontSize: 11, color: '#6B7280' }}>{f.flightDate} {f.flightDepTime && `@ ${f.flightDepTime}`}{f.flightArrTime && ` - ${f.flightArrTime}`}</div>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Room Assignments */}
                      {memberDetail.hotels?.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#1E40AF', marginBottom: 6 }}>🏨 Room Assignments</div>
                          {memberDetail.hotels.map(h => (
                            <div key={h.id} style={{ backgroundColor: '#EFF6FF', padding: 8, borderRadius: 6, marginBottom: 4, fontSize: 13 }}>
                              <div style={{ fontWeight: 500 }}>{h.hotelName}</div>
                              {h.roomType && <div style={{ color: '#4B5563' }}>Room: {h.roomType}</div>}
                              {h.hotelConfirm && <div style={{ fontSize: 11, color: '#6B7280' }}>Conf #: {h.hotelConfirm}</div>}
                              {h.sharingWith && <div style={{ fontSize: 11, color: '#6B7280' }}>Sharing: {h.sharingWith}</div>}
                              {h.hotelGuests && <div style={{ fontSize: 11, color: '#6B7280' }}>Guests: {h.hotelGuests}</div>}
                              <div style={{ fontSize: 11, color: '#6B7280' }}>Breakfast: {h.breakfastIncluded ? <span style={{ color: '#166534' }}>Yes</span> : <span>No</span>}</div>
                              {h.hotelRemarks && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4, padding: 6, backgroundColor: '#DBEAFE', borderRadius: 4 }}>📝 {h.hotelRemarks}</div>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Luggage Tag */}
                      {memberDetail.luggageTag && (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#7C3AED', marginBottom: 6 }}>🏷️ Luggage Tag</div>
                          <img
                            src={memberDetail.luggageTag}
                            alt="Luggage Tag"
                            style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6 }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div style={{ display: 'none', padding: 8, backgroundColor: '#FEF3C7', borderRadius: 6, color: '#92400E', fontSize: 11 }}>
                            ⚠️ Image not available
                          </div>
                        </div>
                      )}

                      {!memberDetail.flights?.length && !memberDetail.hotels?.length && !memberDetail.luggageTag && (
                        <div style={{ color: '#9CA3AF', fontSize: 13, fontStyle: 'italic' }}>No travel details added yet</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Contact Organizer Section */}
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>💬 Contact Organizer</h3>

          {/* Message Threads - Show first if any exist */}
          {myMsgs && myMsgs.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>Your conversations:</p>
              {myMsgs.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)).map(thread => {
                const threadMsgs = getThreadMessages(thread);
                const threadFull = threadMsgs.length >= 10;
                const isExpanded = expandedThread === thread.id;
                const firstMsg = threadMsgs[0];
                const preview = firstMsg?.text?.substring(0, 60) + (firstMsg?.text?.length > 60 ? '...' : '');

                return (
                  <div key={thread.id} style={{ backgroundColor: '#F9FAFB', borderRadius: 12, marginBottom: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                    {/* Thread Header - Always visible, clickable */}
                    <div
                      onClick={() => setExpandedThread(isExpanded ? null : thread.id)}
                      style={{ padding: 16, cursor: 'pointer', backgroundColor: isExpanded ? '#EFF6FF' : '#F9FAFB' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: '#6B7280' }}>Started {new Date(thread.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: '#6B7280' }}>{threadMsgs.length}/10</span>
                          <span style={{ color: '#6B7280', fontSize: 12 }}>{isExpanded ? '▼' : '▶'}</span>
                        </div>
                      </div>
                      {!isExpanded && (
                        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#4B5563', fontStyle: 'italic' }}>"{preview}"</p>
                      )}
                    </div>

                    {/* Expanded Thread Content */}
                    {isExpanded && (
                      <div style={{ padding: '0 16px 16px 16px' }}>
                        {/* All messages */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                          {threadMsgs.map((msg, idx) => (
                            <div key={msg.id || idx} style={{ backgroundColor: msg.senderRole === 'admin' ? '#ECFDF5' : '#DBEAFE', borderRadius: 8, padding: 10, borderLeft: `3px solid ${msg.senderRole === 'admin' ? '#047857' : '#3B82F6'}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: msg.senderRole === 'admin' ? '#047857' : '#3B82F6' }}>{msg.senderRole === 'admin' ? 'Organizer' : 'You'}</span>
                                <span style={{ fontSize: 10, color: '#9CA3AF' }}>{new Date(msg.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                              </div>
                              <p style={{ margin: 0, fontSize: 14, color: '#1F2937', whiteSpace: 'pre-wrap' }}>{msg.text}</p>
                            </div>
                          ))}
                        </div>

                        {/* Reply form or limit message */}
                        {threadFull ? (
                          <div style={{ padding: 10, backgroundColor: '#FEE2E2', borderRadius: 6, textAlign: 'center', fontSize: 12, color: '#991B1B' }}>
                            Thread limit reached (10 messages). Start a new thread above.
                          </div>
                        ) : replyingTo === thread.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..." style={{ ...inp, minHeight: 50 }} autoFocus />
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => { setReplyingTo(null); setReplyText(''); }} style={{ flex: 1, padding: '6px 12px', backgroundColor: 'transparent', color: '#6B7280', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                              <button onClick={() => handleReply(thread.id)} style={{ ...btn, flex: 1, padding: '6px 12px', fontSize: 13 }}>Send</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => setReplyingTo(thread.id)} style={{ ...btn, padding: '6px 12px', fontSize: 13, width: '100%' }}>Reply</button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* New Message Form - Below threads */}
          <div style={{ backgroundColor: '#F9FAFB', borderRadius: 12, padding: 16 }}>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>{myMsgs && myMsgs.length > 0 ? 'Start a new conversation:' : 'Send a message to the trip organizer:'}</p>
            <textarea value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Type your message here..." style={{ ...inp, minHeight: 60, marginBottom: 12 }} />
            <button onClick={handleSend} style={{ ...btn, width: '100%' }}>Send Message</button>
          </div>
        </div>
      </main>
    </div>
  );
}

// ========== CURRENCY CONVERTER ==========
function CurrencyConverter({ onClose }) {
  const [amount, setAmount] = useState('100');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('JPY');
  const [rates, setRates] = useState(null);
  const [ratesUpdated, setRatesUpdated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currencies = [
    { code: 'JPY', name: 'Japanese Yen', flag: '🇯🇵', symbol: '¥' },
    { code: 'USD', name: 'US Dollar', flag: '🇺🇸', symbol: '$' },
    { code: 'TWD', name: 'Taiwan Dollar', flag: '🇹🇼', symbol: 'NT$' },
    { code: 'CNY', name: 'Chinese Yuan', flag: '🇨🇳', symbol: '¥' },
    { code: 'THB', name: 'Thai Baht', flag: '🇹🇭', symbol: '฿' },
    { code: 'AUD', name: 'Australian Dollar', flag: '🇦🇺', symbol: 'A$' },
  ];

  // Fallback rates (approximate, from JPY base)
  const fallbackRates = { JPY: 1, USD: 0.0067, TWD: 0.21, CNY: 0.048, THB: 0.24, AUD: 0.01 };

  const fetchRates = async () => {
    setLoading(true);
    setError(null);
    try {
      // Frankfurter doesn't support TWD, so we fetch what we can and use fallbacks
      const response = await fetch('https://api.frankfurter.app/latest?from=JPY&to=USD,CNY,THB,AUD');
      if (!response.ok) throw new Error('Failed to fetch rates');
      const data = await response.json();
      // Merge API rates with fallbacks for unsupported currencies (like TWD)
      const jpyRates = { JPY: 1, ...fallbackRates, ...data.rates };
      setRates(jpyRates);
      setRatesUpdated(new Date());
    } catch (err) {
      setError('Unable to fetch exchange rates. Using fallback rates.');
      setRates(fallbackRates);
      setRatesUpdated(new Date());
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!rates) fetchRates();
  }, []);

  const convert = () => {
    if (!rates || !amount) return null;
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return null;

    // Get rates with fallback
    const fromRate = rates[fromCurrency] || fallbackRates[fromCurrency];
    const toRate = rates[toCurrency] || fallbackRates[toCurrency];
    if (!fromRate || !toRate) return null;

    let jpyAmount;
    if (fromCurrency === 'JPY') {
      jpyAmount = numAmount;
    } else {
      jpyAmount = numAmount / fromRate;
    }

    let result;
    if (toCurrency === 'JPY') {
      result = jpyAmount;
    } else {
      result = jpyAmount * toRate;
    }
    return result;
  };

  const getExchangeRate = () => {
    if (!rates) return null;
    if (fromCurrency === toCurrency) return 1;

    const fromRate = rates[fromCurrency] || fallbackRates[fromCurrency];
    const toRate = rates[toCurrency] || fallbackRates[toCurrency];
    if (!fromRate || !toRate) return null;

    if (fromCurrency === 'JPY') return toRate;
    if (toCurrency === 'JPY') return 1 / fromRate;
    return toRate / fromRate;
  };

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const formatNumber = (num, currency) => {
    if (num === null) return '—';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: currency === 'JPY' ? 0 : 2,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2
    }).format(num);
  };

  const result = convert();
  const rate = getExchangeRate();
  const fromCurr = currencies.find(c => c.code === fromCurrency);
  const toCurr = currencies.find(c => c.code === toCurrency);

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 400, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>💱 Currency Converter</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6B7280' }}>×</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>Amount</label>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="Enter amount"
            style={{ width: '100%', padding: 12, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 18, fontWeight: 600, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>From</label>
            <select value={fromCurrency} onChange={e => setFromCurrency(e.target.value)} style={{ width: '100%', padding: 12, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 15, backgroundColor: 'white' }}>
              {currencies.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
            </select>
          </div>
          <button onClick={swapCurrencies} style={{ marginTop: 20, padding: '8px 12px', backgroundColor: '#EFF6FF', border: '1px solid #3B82F6', borderRadius: 8, cursor: 'pointer', fontSize: 16 }}>⇄</button>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>To</label>
            <select value={toCurrency} onChange={e => setToCurrency(e.target.value)} style={{ width: '100%', padding: 12, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 15, backgroundColor: 'white' }}>
              {currencies.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ backgroundColor: '#F9FAFB', borderRadius: 12, padding: 20, textAlign: 'center', color: '#6B7280' }}>Loading rates...</div>
        ) : (
          <>
            {error && <div style={{ backgroundColor: '#FEF2F2', borderRadius: 12, padding: 12, marginBottom: 16, color: '#991B1B', fontSize: 13 }}>{error}</div>}
            {result !== null && (
              <div style={{ backgroundColor: '#EFF6FF', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 8 }}>{fromCurr?.flag} {formatNumber(parseFloat(amount), fromCurrency)} {fromCurrency}</div>
                  <div style={{ fontSize: 13, color: '#3B82F6', marginBottom: 8 }}>↓</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#1E40AF' }}>{toCurr?.flag} {toCurr?.symbol}{formatNumber(result, toCurrency)}</div>
                  <div style={{ fontSize: 14, color: '#3B82F6', marginTop: 4 }}>{toCurrency}</div>
                </div>
                {rate && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #DBEAFE', textAlign: 'center' }}>
                    <span style={{ fontSize: 13, color: '#6B7280' }}>Rate: 1 {fromCurrency} = {formatNumber(rate, toCurrency)} {toCurrency}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {ratesUpdated && <span style={{ fontSize: 11, color: '#9CA3AF' }}>Updated: {ratesUpdated.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
          <button onClick={fetchRates} disabled={loading} style={{ padding: '6px 12px', backgroundColor: 'white', color: '#3B82F6', border: '1px solid #3B82F6', borderRadius: 6, fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}>🔄 Refresh</button>
        </div>
      </div>
    </div>
  );
}
